/** Battle chrome: hex marks, the attack cursor, the initiative queue, the
 * hovered-stack panel and the command console.
 *
 * None of it decides anything (I10). The reachable set, the legal attack
 * positions and which commands are offered arrive as plain data on `UiState`,
 * computed by the app from `computeReach` / `attackPositions` /
 * `enumerateLegalBattleCommands`; this file only paints them, and reports which
 * button the pointer is over. The one piece of real logic here is
 * `attackApproach`, and it is a *presentation* rule: given a set the kernel
 * already declared legal, which of them is the player pointing at.
 *
 * The paragraph that stood here said "The classic never draws a grid", and that is
 * false: the classic outlines every cell of the board and darkens one side of the
 * movement boundary.
 *
 * It used to say so on the authority of two filenames -
 * A reference screenshot and
 * A reference screenshot. **Neither
 * file exists.** `find /` returns nothing for either name and there is no
 * `refs/` directory anywhere on this machine, so no number cited to them was
 * ever re-checkable by anyone who came after. Four such names are cited
 * thirteen times across this repo's shipped source; this pass re-derives what
 * can be re-derived and says so in the comment where it cannot.
 *
 * What replaces them is the critic's reference set (a reference screenshot,
 * provenance per file in its `INDEX.md`): publisher-supplied classic gallery frames
 * and wiki frame grabs of the shipped game. Two of them carry a drawn grid:
 *
 *   a reference screenshot           1600x1200, the gallery's 2x upscale of a native
 *                             800x600 siege frame. **Grid and darkened region
 *                             in the same frame**, which is what this file's
 *                             treatment needs and what the deleted file was
 *                             said to show.
 *   `CLASSIC_Battlefield.jpg`   647x486, a 0.81x downscale of a native 800x600
 *                             open-field frame. Grid on, no region.
 *
 * Measured on `classic16` (2026-08-30), grid pixels cut out of the ground samples
 * at the 85th luma percentile, five patches inside the region against five
 * outside:
 *
 *   the region is a multiply, not a wash    0.544 of the open ground, a drop of
 *                                           31.9 luma (38.1 against 70.0)
 *   the line is one value on both sides     median RGB (104,124,30) inside the
 *                                           region, (107,130,24) outside it -
 *                                           the ground steps 27.8 luma across
 *                                           that boundary and the line 4.4
 *
 * So the 0.54 multiply the deleted file was said to show is reproduced to three
 * decimals off a file that is on disk. The *colour* it was said to show is not
 * reproduced at all; see `GRID_LINE_COLOR`.
 *
 * Both are JPEGs at 4:2:0, so neither is read one pixel at a time: every figure
 * here is a median over thousands of pixels found by a vertical-ridge detector
 * (a column brighter than its neighbours four px out, sustained over ten rows),
 * with the ground sampled six px to each side - outside the 8x8 block a sharp
 * line rings into - and averaged across both sides so the ringing cancels to
 * first order.
 *
 * What they do **not** settle is whether the region persists between commands
 * or appears only while a stack is being pointed at. `classic16` is a still, and on
 * it a stack is selected (the Watcher Orb top-left carries the yellow selection
 * outline); `CLASSIC_Battlefield` has the lattice on and no darkened region
 * anywhere, which is consistent with either answer. So "the marking is a shaded
 * region" is measured; "the marking is always on screen" is not, and if it ever
 * needs deciding it needs a source these files are not.
 *
 * Acting on the wrong premise produced an affordance that read inside-out. The
 * reach was drawn as one *lit* region: `0xffc25c` at 0.26, which composites to
 * `0.74L + 52.2` and measured +21.8 luma over the ground on the shipped 1080p
 * frame - against a backdrop whose own 40x40 tile means run 59.2 to 170.8, sd
 * 20.2, over the open field. The marking was at the noise floor of the terrain
 * it was drawn on. And because obstacle cells are holes in the reachable set,
 * the only part of it with any contrast was the boundary lip, which traces the
 * hole - so the cells that read as marked were exactly the impassable ones,
 * a hexagon around every rock and dead tree.
 *
 * So the reach is now a multiply, not a wash, and the outline is per cell.
 * A multiply is the one marking that cannot be swallowed by the backdrop: it is
 * a constant *fraction* of whatever it is drawn on, so it holds its contrast on
 * bright sand and on dark lava alike, which a fixed additive lift does not.
 *
 * **Which side of the boundary the multiply goes on is not settled by any of
 * these files, and was assumed.** `classic16` is a still: it shows a darkened
 * region with a hex boundary and one selected stack, and nothing in it says
 * whether the dark side is where that stack may go or where it may not - that
 * would take the stack's speed, which a screenshot does not carry. Three
 * shipped rounds read it as the reach, and the consequence is visible in every
 * one of the seven arena shots: two thirds of the board darkened, the invitation
 * in shadow, and an unshaded hexagon standing under every rock because an
 * obstacle is a hole in the reachable set. The shade went onto the complement
 * for a round, and has come back to the reach - cell by cell, the way the classic
 * stamps it - see `paintReachRegion` for the reference frame that settled it.
 *
 * The sentence that used to end this paragraph - "no grid is drawn outside the
 * reachable region" - was the next defect, and it is worth being precise about
 * why, because the multiply above is *not* the thing that was wrong. Measured on
 * the shipped 1920x1080 frame by sampling the outline on its own geometry (cell
 * edges sit at arena `x = 175 + 30k`, spanning a row centre by `ROW_PITCH/3`):
 * the line ran **+52.2 luma** over the ground inside the region and **-2.9**
 * outside it. Not faint outside: absent. So the only thing telling a player
 * where the board ended was the edge of the shading, and a reach that covers two
 * thirds of a 15x11 board therefore put a hard hex-quantised value step across
 * the middle of the picture with nothing on the far side of it - which reads as
 * a lighting change in the terrain, because a lighting change is the only thing
 * a value step with no drawn edge can be.
 *
 * The classic does both at once and that is the reference to copy. On a reference screenshot
 * the darkened region and the full board grid are on the screen together, and
 * the line holds its own value across the boundary: median (104,124,30) inside
 * against (107,130,24) outside, a step of 4.4 luma where the ground under it
 * steps 27.8. One line, one value, is what keeps the lattice legible across the
 * boundary and what makes the boundary read as the edge of a marking rather
 * than as shade on the ground. `paintBoardGrid` draws it, over the shading and
 * over every cell.
 *
 * A hex is still outlined under the cursor and around the acting stack.
 */

import { Container, Graphics, Sprite } from "pixi.js";
import type { BattleState, ContentRegistry } from "@game/core";
import { F_ALIVE, hexNeighbours } from "@game/core";
import { BitmapLabel } from "../text/index.js";
import type { AtlasLike } from "./deps.js";
import { hasKey } from "./deps.js";
import {
  CHAMFER, COLOR, GLYPH_ART, PRIMARY_FLOOR, chamfered, chargeFor, engrave,
  glyphPlacement, grain, paintCommandBand,
  paintIconSocket, paintInset, paintMessageStrip, paintMeter, paintPanel, paintPlaque,
  paintPortraitLip, paintPortraitSocket, paintStandard, paintStoneButton,
  paintStonePlate,
} from "./theme.js";
import type { ButtonMood, GlyphId } from "./theme.js";
import {
  ARENA_H, ARENA_W, BOARD_CELL_EDGES, CONSOLE_H, FIELD_LEFT, FIELD_W, asHex, attackApproach,
  hexCentre, hexPolygon, markerEdges, markerPolygons, px, regionEdges,
  stackFootprintOf,
} from "./grid.js";
import type { StackFootprint } from "./grid.js";
import type { PixelPoint } from "../coords.js";
import { POSE, creatureArtRef, unitFrameKey } from "./keys.js";
import { FRAME_W, groundLevel, groundTones } from "./arena.js";
import { DEFAULT_SIDE_COLORS, type SideColors } from "./stacks.js";

/**
 * The things the console can ask the app to do.
 *
 * `spellbook` is offered like every other command - the console paints it and
 * reports the press, and the app decides what a press means (I10). It is the
 * one the classic puts on the console that this screen had as a line of text floating
 * over the top-left of the battlefield.
 *
 * A button is only clickable when the app names it in `UiState.enabled`, and
 * that matters here more than usual: a host that paints `spellbook` without
 * routing it will fall through whatever `else` its command handler ends in, so
 * the opt-in has to be the host's, in the same file that handles the press.
 */
export type BattleAction =
  | "spellbook" | "wait" | "defend" | "retreat" | "surrender"
  /**
   * The three added with the hero's blow, the faction racial and creature
   * activated abilities. They sit with the other turn-spending buttons on the
   * right: each of them costs the same thing `wait` and `defend` cost, and the
   * app only names one in `UiState.enabled` when the kernel is actually
   * offering it, so a hero with a full book and no racial sees five buttons and
   * a Oakwarden's owner sees six.
   */
  | "strike" | "racial" | "activate";

/**
 * Reading order along the console: four buttons, the narration strip, four more,
 * which is the shape the reference uses - a group hard against each margin with
 * the reading window between them.
 *
 * The **count** is four and four because of where the strip has to sit, not
 * because of what the eight commands mean. The strip is the console's subject
 * and it has to be centred on the frame's own axis; a strip centred between two
 * groups is centred on the frame only when the two groups are the same width.
 * With two on the left and six on the right the strip's middle landed 108 arena
 * pixels (162 device) left of the arena's centre line, and no amount of padding
 * fixes that while one group is three times the other.
 *
 * Within the four and four the split is still the old one as far as it goes: the
 * two that end the battle lead the left group, and the two that end the acting
 * stack's turn - wait and defend, the pair the classic puts last - close the right.
 * The book and the faction seal go left with the concessions because they are
 * the army's own charges rather than this stack's; the hero's blow and the
 * creature's ability go right because they resolve on the board this turn.
 */
export const BATTLE_ACTIONS: readonly BattleAction[] =
  ["surrender", "retreat", "spellbook", "racial", "strike", "activate", "wait", "defend"];

/**
 * Who is commanding one of the armies: the face and the name at the end of the
 * console.
 *
 * `BattleHeroState` carries a `HeroId` - a runtime instance id like `hero:1` -
 * and no def id, so the battle state on its own cannot name its own commander
 * or find a portrait for him. The app can: it holds the `GameState` the hero
 * lives in, and `AdventureShell` already resolves `HeroId -> portrait key` for
 * the after-action report. So the app says who this is, and the console paints
 * whoever it is told.
 */
export interface CommanderView {
  readonly name: string;
  /** Atlas key of the painted portrait, e.g. `portrait.sunspire.halethas`. */
  readonly portrait: string;
}

export interface UiState {
  /** Slot whose turn it is, or -1. */
  readonly activeStack: number;
  /** Head hexes the active stack may move to. */
  readonly reachable: readonly number[];
  /** Head hexes from which the active stack may strike `hoverStack`. */
  readonly attackFrom: readonly number[];
  readonly hoverHex: number | null;
  readonly hoverStack: number | null;
  /**
   * The hexes the active stack would walk through to reach `hoverHex`, head hex
   * first. Optional: the app computes it with the kernel's own pathfinder, and
   * the renderer never derives one - a straight chain of arrows over a route the
   * rules would not take is worse than no arrows at all.
   */
  readonly path?: readonly number[];
  readonly pointer: { readonly x: number; readonly y: number } | null;
  /** What the console reads, e.g. "Round 3 - your move". The app writes it. */
  readonly status: string;
  /** Which command buttons the app will accept right now. */
  readonly enabled: readonly BattleAction[];
  /**
   * The two commanders, attacker first. Absent - or `null` for one side - falls
   * back to a stand-in drawn from the army's own faction roster; see
   * `commanderOf`.
   */
  readonly commanders?: readonly [CommanderView | null, CommanderView | null];
  /**
   * Draw the cell lattice over the whole board. Off unless the player asks,
   * which is the classic's own default: its field is a painting with a movement
   * shade on it, and the hex outlines are a toggle (`Show battle grid`) that is
   * off out of the box. The hover outline and the acting stack's marker do
   * not depend on it; the reach shading is drawn while the pointer is on the
   * board, or always when this is set - see `paintReach`.
   */
  readonly showGrid?: boolean;
  /**
   * Shade every cell the acting stack can reach - The classic's "movement shadow",
   * an option a player switches on, off by default in the classic. It used to be
   * painted whenever the pointer was on the board, and for a speed-12 flyer
   * that outlined the entire field the moment the mouse crossed it: a critic
   * measured "hovering one hex draws the full hex grid". Under the pointer
   * The classic answers "can I go there" with the cursor and the status line, not
   * with the board.
   */
  readonly showReach?: boolean;
  /**
   * Ring the acting stack's cells on the ground. Off by default: the classic draws
   * nothing round the active creature with the grid off - the HD mod's
   * "highlight active stack" is an option, and so is this. The queue strip
   * and the creature's own idle say whose move it is.
   */
  readonly showActiveMarker?: boolean;
  /**
   * The stack whose figures the info panel shows. The classic shows a stack's
   * numbers on **right-click**, and hover only changes the cursor; the panel
   * used to follow `hoverStack`, so crossing an enemy popped a card over the
   * field. `null` or absent: no panel.
   */
  readonly inspectStack?: number | null;
}

export const EMPTY_UI: UiState = {
  activeStack: -1,
  reachable: [],
  attackFrom: [],
  hoverHex: null,
  hoverStack: null,
  pointer: null,
  status: "",
  enabled: [],
};

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const FULL_VIEW: Rect = { x: 0, y: 0, w: ARENA_W, h: ARENA_H };

// ------------------------------------------------------------- command bar

/**
 * The console, measured off the real thing.
 *
 * This used to cite `refs/battle/h3hd-battle-open-field-ballista-console-
 * 1920x1080.jpg`, which does not exist. Re-measured 2026-08-30 on two files
 * that do - `CLASSIC_Battlefield.jpg` and a reference screenshot, both native 800x600
 * frames - the console is 44 of the frame's 600 rows (7.3%), and along it:
 * four abutting square buttons hard against the left margin, a wide recess
 * carrying one centred line of narration, a scroll-arrow pair, and three or
 * four more abutting buttons hard against the right margin (four on
 * `CLASSIC_Battlefield`, three on `classic16`). Nothing else - no hero portrait
 * plaques, no acting-stack stat card. Both of those were in this console and
 * both are gone; the commanders moved up into the two top corners of the frame
 * and the creature's numbers are what the hover panel is for, which is where
 * The classic puts them too.
 *
 * One detail here was wrong and is corrected rather than dropped: the recess's
 * keyline is **not crimson**, it is the acting player's own colour. On `classic16`
 * it measures RGB (58,12,73) rising to (74,35,78) - the purple player, whose
 * count plates in the same frame are the same purple. On `CLASSIC_Battlefield` at
 * 0.81x no keyline resolves at all. `paintMessageStrip` in `theme.ts` still
 * draws a fixed crimson one; that file is not this one's to change, and the
 * finding is recorded here so it is not lost.
 *
 * The buttons abut, three pixels apart, because in the reference they are one
 * carved strip with seams rather than five plates floating on a bar.
 */
const BAND_H = CONSOLE_H;
// Derived from the band as a *ratio*, not as a fixed inset: the reference's
// buttons stand 66 of a 75px console, which is 88%, and `CONSOLE_H` is not this
// file's to set. It has since been cut from 68 to 50 (6.94% of the frame,
// inside the 6.7-7.2% reference band - see `TOPBAR_H` in `grid.ts`) and the
// buttons came down with it: 44 arena pixels, which is 66 device pixels at
// 1920x1080, the reference's own figure at the reference's own resolution.
// `BAND_H - 12` would have given 38 and left the console reading as a strip of
// bare planking with small plates floating in it.
const BTN_H = Math.min(56, Math.round(BAND_H * 0.88));
/**
 * Square, as the reference's are: "four abutting square buttons hard against the
 * left margin". They were 1.15 times as wide as they are tall, and the 7 arena
 * pixels apiece that bought were the difference between a strip that can be
 * centred on the frame and one that cannot - eight of them is 56 pixels, and the
 * centred strip needs 42 of that.
 */
const BTN_W = BTN_H;
/** Seam between two buttons of the same group. */
const BTN_SEAM = 3;
/** Margin from the window edge to the first button, and to the message strip. */
const CONSOLE_PAD = 12;
/** How many actions sit in the left-hand group; the rest go right. */
const LEFT_GROUP = 4;
/**
 * How much of the console's width the narration strip takes.
 *
 * It used to take every pixel the buttons left, which at 1920x1080 was a
 * 1442x50 device trough carrying one 220px string: 85% of it empty, and it read
 * as a bar with a caption dropped in rather than as a reading window. The
 * reference's own strip is 355 of a 710px console - **50%** - with a 120px line
 * in it, so it is two thirds empty there too and the difference is entirely the
 * proportion of the bar it claims.
 *
 * 0.52 rather than 0.50 because the console is 1280 arena pixels wide here and
 * two four-button groups plus their margins already spend 394 of it; 0.52 is the
 * largest strip that still leaves air on both sides of a strip centred on the
 * axis, and it is the side of the reference to err on.
 *
 * **0.34 now, and the 18% it gave up is the two commanders' plaques.** They
 * used to float in the top corners of the frame over the painted sky, where
 * they collided with the back rank once the board grew to fill its strip
 * (`ROW0_Y`): a tier-7 sprite on column 0 reaches the top of the frame. The classic
 * itself has no hero panel on this screen at all - the commanders are figures
 * on the field - but a plaque has to live somewhere if it lives at all, and
 * the console is the one band of chrome that is already there. Each plaque sits
 * between a button group and the narration strip; 0.34 is 435 arena pixels of
 * strip, wider than the reference's own 355-of-710 in proportion to the buttons
 * it sits between, and leaves 209 for each plaque against `HERO_W` = 196.
 */
const MESSAGE_SHARE = 0.34;
/** Air between a commander's plaque and the blocks either side of it. */
const PLAQUE_GAP = 8;
/**
 * The extent every console glyph is normalised to, in arena pixels.
 *
 * The button's interior is `BTN_W - 8` = 36, and `engrave` lays the same drawing
 * five times over a 3.5px range of `dy`, so a glyph normalised to 27 paints a
 * 27x30 box in a 36x36 hole - about 4 pixels of air on every side. The eight
 * drawings used to be laid down at one `s` regardless of their own extents,
 * which ran from 0.97s to 1.85s: the widest two ran into their bezels and the
 * book was 40% smaller than the shield next to it.
 *
 * 27 rather than 24 because the glyphs are filled objects now rather than
 * outlines (`engrave`), and the reference's own icons fill more of their button
 * than an outline set has to: measured on `rig/ref/battle.png`, the book, flag,
 * runner and swords each span about 34 of a 44-pixel interior - 0.77 - against
 * the 0.67 that 24 buys here. 27 is 0.75.
 */
const GLYPH_EXTENT = 27;

/**
 * A commander's plaque: in the console, between a button group and the
 * narration strip.
 *
 * It was 46 tall in the top corner of the frame, over the painted sky. That
 * placement had no reference behind it - every battle screen in the reference
 * set is the classic, the original or the expansion, and on all of them the commanders are figures on the
 * field with no hero panel over the board - and it stopped being safe the
 * moment the board grew to fill its strip: with `ROW0_Y` at 130 a tier-7 sheet
 * on column 0 reaches the top of the frame, straight through a corner plaque.
 *
 * So the plaque is as tall as a console button, `BTN_H` = 44, and it reads the
 * same way the buttons do: one carved strip along the foot of the frame. The
 * name row is a 22px bitmap row (the `text` face at magnification 3 is an 18px
 * cap, and 2 is not available at a 1.5x arena scale - see `snapMagnification`)
 * and the stats row at `PLAQUE_ROW_Y` is 14 deep, which ends at 40 inside a
 * 44px plate. `battle-console.spec.ts` asserts that the plaque, the strip and
 * the buttons are disjoint and all inside the band.
 */
export const HERO_H = BTN_H;
const HERO_W = 196;
/** The plaque's one stats row: the cut sockets are this big, at this y. */
const PLAQUE_SOCK = 14;
const PLAQUE_ROW_Y = 26;
/**
 * The turn queue: a strip of creature plates across the **top of the field**.
 *
 * It ran down the right-hand leather as a rail of 62px portrait plates, and a
 * critic measured what that cost: the field was 72.6% of the viewport's width
 * against the classic's 82.5% and sat off-centre between a 125px left rail and a
 * 130px right rail plus the queue. The references that draw a queue at all -
 * The classic HD and the expansion - draw it as a horizontal strip of small icons over the top
 * edge of the field, above the horizon, and the classic draws none; nothing in the set
 * puts a column of plates beside the board. So the strip lies across the top,
 * centred on the field's own axis, just under the frame's gilt top rail
 * (`FRAME_TOP` in `arena.ts` is 18), over the painted sky where the board has
 * nothing to lose.
 *
 * The plate is 30x40 arena pixels: 45x60 device at 1920x1080 and 32x43 at
 * 1366x768, against the expansion's 32px icons on an 800px frame (5.3% of the width;
 * ours is 2.3% at 1080p, a hair over half of that, because a plate here holds a
 * whole creature fitted to its niche rather than a head-and-shoulders portrait,
 * and needs the height). `battle-queue-plate.spec.ts` pins the plate to the
 * The expansion band and the strip to the field's axis.
 */
/**
 * **Docked on the rail, not hung under it.** `FRAME_TOP` is 12 and the strip
 * starts at 13: one row of shadow line and no more, so nothing of the arena
 * shows between the frame and the strip.
 *
 * A critic named this strip before the reveal in four of six blind rounds - the
 * single fastest tell in the frame - and what it named was "a gold-framed strip
 * of 13-14 portrait tiles floating over the painted horizon, centred, with
 * arena visible above AND below it". Three things made it float and all three
 * are gone: it was hung 7 rows below the rail, it was cut to its own contents
 * so the painting ran past both its ends, and it carried a double gold bevel
 * heavier than the arena's own frame. It is now a full-width trough on the
 * rail, in the frame's own gold, and it reads as part of the frame.
 *
 * The classic and HD draw no queue at all; the expansion's is a slim strip of small square
 * creature icons flush with the top edge of the battlefield, and it carries no
 * count on the icons. That is what this is now.
 */
const QUEUE_Y = 13;
/**
 * Square plates, 29x29 rather than 30x40 portraits.
 *
 * The strip's whole cost is height off the picture, and after `paintSky` there
 * is a sky up there to lose: a 40-row plate plus furniture is 46 arena rows, 69
 * device, which is more than the whole visible sky band. Square costs 15 rows
 * and loses nothing that identifies a creature - the thumb is fitted to its
 * niche either way (`queueThumb`), so a square plate draws the same creature at
 * three quarters the height: 43x43 device at 1080p and 31x31 at 1366x768,
 * against the expansion's 32 on an 800-wide frame.
 */
const QUEUE_CARD_W = 29;
const QUEUE_CARD_H = 29;
const QUEUE_GAP = 2;
/** Band furniture inside each end: the keyline, and air before the first plate. */
const QUEUE_PAD = 2;
/**
 * How much of the field's width the strip may take. Fourteen plates - seven a
 * side, the most the kernel deals - come to 459 of the field's 930; a queue
 * longer than the strip says "+N" at its end (`MORE_W`) rather than shrinking
 * the plates, which is the same rule the rail used.
 */
const QUEUE_MAX_SHARE = 0.8;
/** Width reserved at the end of a truncated strip for its "+N". */
const MORE_W = 24;
/**
 * Where the stack count sits on a queue plate: a small chamfered tab in the
 * **bottom-right corner of the niche**, sized to its own digits.
 *
 * It used to be a full-width strip across the foot of the plate, and it cost
 * the creature the rows it occupied twice over - once for the strip itself and
 * once for the clearance above it - so the sprite was fitted into `cardH - 17`
 * and came out 30 device pixels tall. A creature sheet frame is about 150x290,
 * and the plate is wider than it is tall, so the niche has width to spare and no
 * height at all: the number goes in the spare width, the creature stands a
 * little left of centre, and the sprite gets the plate's whole depth. That is
 * also where the classic puts a stack's count on the battlefield itself - a small
 * plate off the creature's bottom-right corner, not a caption under it.
 *
 * `CARD_THUMB_CENTRE` is how far left of the niche's centre the creature
 * stands, so the tab clears it.
 */
const CARD_THUMB_CENTRE = 0.42;
/** Air between the creature's feet and the cut floor of the niche. */
const CARD_THUMB_FLOOR = 2;

/**
 * Where every block of the console sits, in arena pixels. Pure, so the geometry
 * can be asserted without a GPU - `battle-console.spec.ts` is that assertion.
 *
 * The strip is laid out **from the frame's axis outward**, not from whatever gap
 * the two button groups leave. Laid out the other way it inherits their
 * asymmetry: with the groups at two and six the strip ran 129..935 of a 1280
 * arena-pixel frame and its middle sat 108 pixels left of the middle of the
 * board it captions, which on a screen whose every other element is mirrored
 * about that axis reads as the whole console having slipped.
 */
export function consoleLayout(view: Rect): {
  readonly buttons: readonly Rect[];
  readonly message: Rect;
  /**
   * The two commanders' plaques, attacker's left. Zero-width when the window
   * is too narrow to carry them beside the strip, in which case the strip
   * takes the room.
   */
  readonly heroes: readonly [Rect, Rect];
} {
  const y = Math.round((BAND_H - BTN_H) / 2);
  const buttons: Rect[] = [];
  let x = Math.round(view.x + CONSOLE_PAD);
  for (let i = 0; i < LEFT_GROUP; i++) {
    buttons.push({ x, y, w: BTN_W, h: BTN_H });
    x += BTN_W + BTN_SEAM;
  }
  const leftEnd = x - BTN_SEAM;
  const rightCount = BATTLE_ACTIONS.length - LEFT_GROUP;
  const rightW = rightCount * BTN_W + (rightCount - 1) * BTN_SEAM;
  let rx = Math.round(view.x + view.w - CONSOLE_PAD - rightW);
  // The near edge of the right-hand group. `rx` runs on past its far edge.
  const rightStart = rx;
  for (let i = 0; i < rightCount; i++) {
    buttons.push({ x: rx, y, w: BTN_W, h: BTN_H });
    rx += BTN_W + BTN_SEAM;
  }
  // The strip is centred on the frame's own axis and sized to a share of the
  // console, not stretched across whatever the buttons leave. Two things then
  // have to hold, and the width gives way to both: it must clear each group by a
  // margin, and it must still be the console's subject rather than a caption.
  const axis = view.x + view.w / 2;
  const room = 2 * Math.min(axis - leftEnd - CONSOLE_PAD, rightStart - CONSOLE_PAD - axis);
  // The plaques take what the strip leaves on each side, and give way first:
  // below a readable width they are dropped and the strip keeps its share.
  let mw = Math.max(40, Math.round(Math.min(view.w * MESSAGE_SHARE, room)));
  const side = (room - mw) / 2 - 2 * PLAQUE_GAP;
  const hw = side >= HERO_W ? HERO_W : side >= 120 ? Math.floor(side) : 0;
  if (hw === 0) mw = Math.max(40, Math.round(Math.min(view.w * 0.52, room)));
  const mx = Math.round(axis - mw / 2);
  const mh = BTN_H;
  // Centred in the gap between the group and the strip, so both margins read
  // as the same seam.
  const lx = Math.round((leftEnd + mx) / 2 - hw / 2);
  const rxp = Math.round((mx + mw + rightStart) / 2 - hw / 2);
  return {
    buttons,
    message: { x: mx, y, w: mw, h: mh },
    heroes: [{ x: lx, y, w: hw, h: HERO_H }, { x: rxp, y, w: hw, h: HERO_H }],
  };
}

/**
 * Where the turn-order rail sits: down the right margin, in the leather flank.
 * (The commanders' plaques used to be laid out here too; they are the
 * console's now - see `consoleLayout`.)
 *
 * Both were in a full-width band across the top of the frame. The band is gone
 * (`TOPBAR_H`), and the reason is a measurement rather than a preference: it
 * and the console together spent 15.6% of the frame's height on chrome, against
 * 6.7-7.2% across five the classic and the expansion references - not one of which spends a
 * pixel of height on a turn queue. Rotating the queue into the right margin
 * took ours to 9.4%, and cutting `CONSOLE_H` from 68 to 50 took it the rest of
 * the way to 6.94%. (A sixth figure, 10.0% for the expansion HD, used to be quoted here
 * as the top of that band; it is 65/650 on a windowed screenshot whose bottom
 * chrome is the console plus an adventure status strip, and it does not survive
 * normalising - see `TOPBAR_H` in `grid.ts`.)
 *
 * The rail is still cut to its own contents rather than to the window. It used
 * to be cut the full width of the band with the plates centred in it, which
 * left it 60.7% covered - measured on a shipped 1920x1080 shot, a 1147px run of
 * plates in an 1890px trough with 372 and 371 bare pixels either side. A trough
 * is only a rail if something rides in all of it.
 */
export function initiativeLayout(view: Rect, slots: number, compact = false): {
  /** The whole strip: band, plates and the "+N" if there is one. */
  readonly rail: Rect;
  readonly cardW: number;
  readonly cardH: number;
  readonly gap: number;
  /** The plate run's width. */
  readonly run: number;
  /** Top-left of the first plate. */
  readonly x0: number;
  readonly y0: number;
  /** Band furniture inside each end. */
  readonly pad: number;
  /** How many of `slots` the strip actually draws. Never more than `slots`. */
  readonly shown: number;
  /** How many it had to leave off the end. `slots - shown`. */
  readonly hidden: number;
  /**
   * Width reserved at the end of the strip for the "+N". Zero when the whole
   * queue is shown, and the third and last thing the strip's width is spent
   * on: plates, the two paddings, and this.
   */
  readonly moreW: number;
} {
  const gap = QUEUE_GAP;
  const cardW = QUEUE_CARD_W;
  const cardH = QUEUE_CARD_H;
  // The trough runs the whole picture, so the plates may run most of it.
  const maxRun = Math.floor((ARENA_W - 2 * FRAME_W) * QUEUE_MAX_SHARE);
  const want = Math.max(1, slots);
  const fitFor = (reserve: number): number =>
    Math.max(1, Math.floor((maxRun - reserve + gap) / (cardW + gap)));
  const n = Math.min(want, fitFor(0) >= want ? fitFor(0) : fitFor(MORE_W));
  const more = n < want ? MORE_W : 0;
  const run = n * (cardW + gap) - gap;
  const railH = cardH + 2 * QUEUE_PAD;
  // **The trough is the picture's width, not the queue's.** Cut to its own
  // contents it was a panel dropped on the painting with the horizon running
  // past both its ends; run edge to edge inside the leather it is a piece of
  // the frame, which is what the expansion's is. The plates ride centred in it.
  //
  // EXCEPT under compact chrome, where that reasoning stops holding. The
  // full-width trough is only free because it sits on the sky, above
  // `FIELD_TOP` - an invariant `battle-console.spec.ts` asserts. Compact chrome
  // CROPS THE SKY (`arenaFit` pins the field in the window and throws the sky
  // away), and the strip is laid at `view.y + QUEUE_Y`, 13 below the visible
  // top rather than below the arena's. With no sky left the band comes down on
  // the back rank, and because the plates ride centred while the armies line up
  // at the left and right margins, the only part of it covering anybody's
  // stacks is its two empty ends. So on a cropped viewport it is cut to its
  // contents: the frame member it was imitating is not there to be part of.
  const railFull = Math.round(ARENA_W - 2 * FRAME_W);
  const railW = compact ? Math.min(railFull, run + more + 2 * QUEUE_PAD) : railFull;
  const railY = Math.round(view.y + QUEUE_Y);
  // On the field's axis, not the window's: every other element of the frame
  // is mirrored about the board, and the two are the same line on the shipped
  // viewports only because the arena is centred in them.
  const axis = FIELD_LEFT + FIELD_W / 2;
  const railX = compact ? Math.round(axis - railW / 2) : Math.round(FRAME_W);
  return {
    rail: { x: railX, y: railY, w: railW, h: railH },
    cardW, cardH, gap, run,
    x0: Math.round(axis - (run + more) / 2), y0: railY + QUEUE_PAD, pad: QUEUE_PAD,
    shown: n, hidden: want - n, moreW: more,
  };
}

/**
 * The inside of one queue plate: where the niche is cut, where its floor is, and
 * how big the creature standing on it comes out.
 *
 * It is a separate pure function because this arithmetic is what went wrong.
 * The niche used to be `cardH - 17` deep - a full-width count strip plus its
 * clearance - and a 150x290 sheet frame fitted into it came out **20 arena
 * pixels tall**, which at 1920x1080 is a 30-pixel speck in a 93-pixel plate. A
 * screenshot shows that as "the queue looks dark"; a number shows it as the
 * fitted height, and `battle-queue-plate.spec.ts` asserts the number.
 *
 * `srcW`/`srcH` are the sheet frame's own pixels. The sheets are around 150x290,
 * so on a plate wider than it is tall the height always binds - which is why the
 * count tab went into the plate's spare width instead of taking a row off its
 * foot.
 */
export function queueThumb(cardW: number, cardH: number, srcW: number, srcH: number): {
  /** The cut niche, in the plate's own coordinates. */
  readonly niche: Rect;
  /** Where the creature's feet go, relative to the niche's top-left. */
  readonly floorY: number;
  readonly scale: number;
  readonly drawnW: number;
  readonly drawnH: number;
} {
  const niche: Rect = { x: 4, y: 4, w: cardW - 8, h: cardH - 8 };
  const floorY = niche.h - CARD_THUMB_FLOOR;
  const scale = Math.min(
    (niche.w * 0.96) / Math.max(1, srcW), (floorY * 0.96) / Math.max(1, srcH),
  );
  return { niche, floorY, scale, drawnW: srcW * scale, drawnH: srcH * scale };
}

/**
 * Where the commander plaque's one stats row puts its sockets and its meter.
 *
 * Coordinates are relative to the plaque's own text column (`tx`, `tw`), so the
 * caller adds `tx`. It is exported because the arithmetic is what went wrong:
 * the plaque is `HERO_H` (36) tall and `paintPlaque` leaves y 2..34, which the
 * name row and one socket row already fill, and the meter used to be laid at
 * `ph - 14` - a second row that landed straight on top of the first. Measured on
 * the shipped 1920x1080 shot, the blue fill crossed all four devices and the
 * word MANA sat on the first one. A test can see that and a screenshot diff of
 * the whole band cannot.
 *
 * The row is cut to its own contents rather than to an even quarter: a cell is
 * a socket plus its own numeral, and the meter takes whatever the four of them
 * leave. Below a readable width the meter is dropped and the digits stay - it is
 * a gloss on a number that is already printed beside it.
 */
export function plaqueStatsRow(tw: number, valueW: readonly number[], manaW: number): {
  readonly sock: number;
  /** The cut socket for each primary, and where its numeral starts. */
  readonly cells: readonly { readonly rect: Rect; readonly textX: number }[];
  readonly meter: Rect | null;
} {
  const sock = PLAQUE_SOCK;
  const n = Math.max(1, valueW.length);
  // The mana digits are right-aligned to the far edge, so they are not part of
  // the run; everything else has to end before they start.
  const avail = tw - manaW - 2;
  const base = valueW.reduce((a, w) => a + sock + 2 + w, 0);
  // The gap between cells is the only slack the row has, and it closes before
  // anything is allowed to run into the digits.
  const gap = n > 1 ? Math.max(0, Math.min(3, (avail - base) / (n - 1))) : 0;
  const cells: { rect: Rect; textX: number }[] = [];
  let cx = 0;
  for (const w of valueW) {
    cells.push({ rect: { x: cx, y: PLAQUE_ROW_Y, w: sock, h: sock }, textX: cx + sock + 2 });
    cx += sock + 2 + w + gap;
  }
  const meterW = avail - cx;
  return {
    sock,
    cells,
    meter: meterW >= 12 ? { x: cx, y: PLAQUE_ROW_Y + 2, w: meterW, h: 10 } : null,
  };
}

/**
 * One line of the console's narration.
 *
 * The ambient scale is passed at construction rather than only through
 * `setDeviceScale` on the next paint: a label built without one is pinned to a
 * magnification of 1 until something repaints it, and
 * `bitmap-magnification.spec.ts` reads the constructions out of this file to
 * make sure no label in the battle screen is ever built that way.
 */
function statusLine(deviceScale: number): BitmapLabel {
  // The caption face at 1 - fifteen rows with two-pixel stems - rather than
  // the nine-row text face at 2. At the 1080p arena scale (1.5) a scale-2
  // label lands on 3 device pixels per texel: a 27-row cap with 3-pixel
  // stems, half again the HD Edition's own console (its caption face is 16
  // rows there) and a monoline typewriter beside the plaques' bold readings.
  // Fifteen rows at magnification 1 is the same face every other caption in
  // the frame is held to, on every window this build targets.
  const t = new BitmapLabel({ fill: 0xf4ecd8, face: "romanCaption", scale: 1, deviceScale });
  t.setAnchor(0.5, 0.5);
  return t;
}

interface ButtonView {
  readonly action: BattleAction;
  readonly node: Container;
  readonly plate: Graphics;
  readonly glyph: Graphics;
  rect: Rect;
  mood: ButtonMood;
}

/**
 * Which drawing each command wears. The action names and the glyph names line up
 * one to one, so this is a rename table and nothing else; the drawings, and the
 * boxes measured off them, live in `theme.ts`.
 */
const GLYPH: Readonly<Record<BattleAction, GlyphId>> = {
  spellbook: "spellbook",
  strike: "strike",
  racial: "racial",
  activate: "activate",
  wait: "wait",
  defend: "defend",
  retreat: "retreat",
  surrender: "surrender",
};

// ------------------------------------------------------------------- layer

export class BattleUi {
  /** Sits under the actors: the reach region and the target rings. */
  readonly under = new Container();
  /** Sits over everything: cursor, queue, console, hovered-stack panel. */
  readonly over = new Container();

  private readonly atlas: AtlasLike;
  private readonly content: ContentRegistry;
  private readonly reach = new Graphics();
  private readonly rings = new Graphics();
  private readonly cursor = new Graphics();
  private readonly bar = new Container();
  private readonly barPanel = new Graphics();
  private readonly barSlots = new Container();
  private readonly console = new Container();
  private readonly band = new Graphics();
  private readonly recess = new Graphics();
  /** The two commanders' plaques, rebuilt from state on every `update`. */
  private readonly plaques = new Container();
  private readonly statusLines: readonly [BitmapLabel, BitmapLabel];
  private readonly buttons: ButtonView[] = [];
  private readonly info = new Container();
  private readonly infoPanel = new Graphics();
  private readonly infoBody = new Container();
  private lastState: BattleState | null = null;
  private lastUi: UiState = EMPTY_UI;
  private colors: SideColors = DEFAULT_SIDE_COLORS;
  private view: Rect = FULL_VIEW;
  /**
   * The scale the arena is drawn at. Every label below is a bitmap glyph, and a
   * glyph texel has to be worth a whole number of device pixels or the same
   * numeral rasterises differently in two plaques of the same frame; see
   * `snapMagnification`. The console rebuilds its labels on every paint, so
   * storing the scale is enough to pin them.
   */
  private deviceScale = 1;
  private hoverAction: BattleAction | null = null;
  private enabled: readonly BattleAction[] = [];
  private message = "";
  private messageRect: Rect = { x: 0, y: 0, w: 1, h: 1 };

  constructor(atlas: AtlasLike, content: ContentRegistry) {
    this.atlas = atlas;
    this.content = content;
    this.under.label = "battle.ui.under";
    this.over.label = "battle.ui.over";
    this.under.addChild(this.reach, this.rings);
    this.bar.addChild(this.barPanel, this.barSlots);
    this.info.addChild(this.infoPanel, this.infoBody);
    this.info.visible = false;

    // The narration, centred in its window and set in the bitmap `text` face.
    //
    // It was a 16px Georgia through a 1.5x magnification, and it was the largest
    // string on the screen: measured on the shipped 1920x1080 shot, "Round 1 -
    // Your move" carried 1,452 distinct colours where "ATTACKER" 700 pixels away
    // carried 16. The `text` face is the one with a full lowercase and the em
    // dash the app's own line uses; the `hud` face has no dash design and would
    // have folded it to `?`.
    //
    // Two labels rather than one with a newline in it: the layout has no line
    // breaker, and the reference's console reads two lines when it has two.
    this.statusLines = [statusLine(this.deviceScale), statusLine(this.deviceScale)];
    this.console.addChild(this.band, this.recess, ...this.statusLines, this.plaques);
    for (const action of BATTLE_ACTIONS) {
      const node = new Container();
      const plate = new Graphics();
      const glyph = new Graphics();
      node.addChild(plate, glyph);
      this.console.addChild(node);
      this.buttons.push({ action, node, plate, glyph, rect: { x: 0, y: 0, w: BTN_W, h: BTN_H }, mood: "disabled" });
    }
    this.over.addChild(this.cursor, this.bar, this.console, this.info);
    this.layoutConsole();
  }

  setSideColors(c: SideColors): void {
    this.colors = c;
  }

  /**
   * The scale the whole arena - and so every label in this chrome - is drawn at.
   * Set before `setViewRect`, which repaints against it.
   */
  setDeviceScale(scale: number): void {
    this.deviceScale = scale > 0 ? scale : 1;
  }

  /** The part of the arena the window actually shows; chrome hugs its edges. */
  setViewRect(rect: Rect): void {
    this.view = rect;
    this.layoutConsole();
    this.paintConsole();
  }

  update(state: BattleState, ui: UiState): void {
    this.enabled = ui.enabled;
    this.lastState = state;
    this.lastUi = ui;
    this.paintReach(state, ui);
    this.paintRings(state, ui);
    this.paintCursor(ui);
    this.paintInitiative(state);
    // The app's line wins whenever it has one; `setMessage` is what a caller
    // playing the event stream pushes into the same slot between frames.
    if (ui.status !== "") this.message = ui.status;
    this.paintConsole();
    this.paintPlaques(state);
    this.paintInfo(state, ui);
  }

  dispose(): void {
    this.under.destroy({ children: true, context: true });
    this.over.destroy({ children: true, context: true });
  }

  // ------------------------------------------------------------- console

  /**
   * Hide the painted command band. Landscape phone draws Wait / Defend / Strike
   * as an HTML touchbar, so the Pixi console would be a second, untappable copy
   * sitting on the front rank.
   */
  setConsoleHidden(hidden: boolean): void {
    this.console.visible = !hidden;
    // Remembered, not just applied: the console being hidden is the signal that
    // the viewport is cropped, and the initiative strip has to know because a
    // cropped viewport has no sky for it to sit on. See `initiativeLayout`.
    this.consoleHidden = hidden;
  }

  /** Which command the pointer is over, in arena pixels, or null. */
  commandAt(p: PixelPoint): BattleAction | null {
    if (!this.console.visible) return null;
    for (const b of this.buttons) {
      if (b.mood === "disabled") continue;
      const r = b.rect;
      if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) return b.action;
    }
    return null;
  }

  /** True for any point on the console band: the field is not under it. */
  overConsole(p: PixelPoint): boolean {
    if (!this.console.visible) return false;
    return p.y >= this.view.y + this.view.h - BAND_H;
  }

  setCommandHover(action: BattleAction | null): void {
    if (this.hoverAction === action) return;
    this.hoverAction = action;
    this.paintConsole();
  }

  /**
   * The narration line the console reads out.
   *
   * The classic's console is a **running commentary** - "The Ballista does 4 damage."
   * over "Next round begins." - and it is the most characteristic thing on the
   * whole screen. Nothing in this package sees a `GameEvent`, so the sentences
   * have to arrive from outside: either the app writes them into `UiState.status`
   * (newline-separated for two lines, as the reference shows two) or whoever
   * plays the event stream pushes them here. Both routes land in the same place.
   */
  setMessage(text: string): void {
    if (this.message === text) return;
    this.message = text;
    this.paintConsole();
  }

  private layoutConsole(): void {
    const v = this.view;
    const top = v.y + v.h - BAND_H;
    this.console.position.set(0, Math.round(top));
    const lay = consoleLayout(v);
    for (let i = 0; i < this.buttons.length; i++) {
      const b = this.buttons[i]!;
      const r = lay.buttons[i]!;
      b.rect = { x: r.x, y: Math.round(top + r.y), w: r.w, h: r.h };
      b.node.position.set(r.x, r.y);
    }
    this.messageRect = lay.message;
  }

  private paintConsole(): void {
    const v = this.view;
    paintCommandBand(this.band, Math.round(v.x), Math.round(v.w), BAND_H);
    for (const b of this.buttons) {
      const on = this.enabled.includes(b.action);
      b.mood = !on ? "disabled" : this.hoverAction === b.action ? "hover" : "idle";
      paintStoneButton(b.plate, BTN_W, BTN_H, b.mood, 5 + BATTLE_ACTIONS.indexOf(b.action) * 7);
      b.glyph.clear();
      // Every glyph is laid down at the size that puts its own measured box on
      // `GLYPH_EXTENT`, and offset by that box's centre - so eight drawings
      // authored around eight different origins come out the same size and
      // optically centred in the same hole. `engrave` shifts the whole set down
      // by up to 2.2 and up by 1.1, so the centre is nudged half a pixel up to
      // keep the composite balanced in the button.
      const art = GLYPH_ART[GLYPH[b.action]];
      const place = glyphPlacement(art, GLYPH_EXTENT);
      b.glyph.position.set(
        Math.round(BTN_W / 2 + place.dx), Math.round(BTN_H / 2 + place.dy - 0.5),
      );
      const mood = b.mood;
      engrave(
        b.glyph,
        (g, colour, alpha, dy, pass) => { art.draw(g, place.size, colour, alpha, dy, pass); },
        mood,
      );
    }
    this.paintMessage();
  }

  /** The two commanders, one plaque each side of the narration strip. */
  private paintPlaques(state: BattleState): void {
    // Rebuilt every paint: the old ones are destroyed, not detached, or their
    // graphics contexts stay registered with the renderer (see `dispose`).
    for (const c of this.plaques.removeChildren()) c.destroy({ children: true, context: true });
    const lay = consoleLayout(this.view);
    for (const side of [0, 1] as const) {
      const r = lay.heroes[side];
      if (r.w === 0) continue;
      const p = this.heroPlaque(state, side, r.w, r.h);
      p.position.set(r.x, r.y);
      this.plaques.addChild(p);
    }
  }

  /** The gilt-keylined recess in the middle of the console, and what it reads. */
  private paintMessage(): void {
    const r = this.messageRect;
    paintMessageStrip(this.recess, r.w, r.h);
    this.recess.position.set(r.x, r.y);
    const lines = this.message.split("\n", 2);
    const shown = lines.filter((l) => l !== "").length;
    const cx = Math.round(r.x + r.w / 2);
    const cy = r.y + r.h / 2;
    for (let i = 0; i < this.statusLines.length; i++) {
      const t = this.statusLines[i]!;
      t.setDeviceScale(this.deviceScale);
      const text = lines[i] ?? "";
      t.text = text;
      t.visible = text !== "";
      // One line sits on the window's own centre; two straddle it by half a line
      // each, so the pair keeps the same centre the single line had.
      const step = shown > 1 ? t.contentHeight * 0.62 : 0;
      t.position.set(cx, Math.round(cy + (i === 0 ? -step : step)));
    }
  }

  /**
   * One army's commander: a painted face, a name, the four primaries and the
   * mana he has left.
   *
   * The classic anchors each end of the console with the hero's portrait, and what was
   * here was a shield glyph and an unnamed faction - a heraldic device standing
   * in for a person, with the device itself chosen by which end of the field the
   * army started on rather than by whose army it was. There are 199 painted
   * portraits in the atlas and this is the screen they belong on.
   */
  private heroPlaque(state: BattleState, side: 0 | 1, w: number, ph: number): Container {
    const node = new Container();
    const h = state.heroes[side];
    const tint = side === 0 ? this.colors.attacker : this.colors.defender;
    const plate = new Graphics();
    paintPlaque(plate, w, ph, 23 + side * 5);
    node.addChild(plate);

    // ---- the commander's face, set into the outer end of the plaque
    //
    // The aperture is **square**, because the pictures are: all 215 painted
    // portraits are packed 256x256, and the opening was 40 wide by 30 deep with
    // a cover fit anchored at the top. A cover fit on a 4:3 hole from a 1:1
    // picture scales to the width and throws away a quarter of the height, so
    // every commander on this screen was cropped to a passport photograph of his
    // own forehead. A square hole takes the whole painting at 1:1.
    const who = this.commanderOf(state, side);
    const bh = ph - 8;
    const bw = bh;
    const bx = side === 0 ? 4 : w - bw - 4;
    paintPortraitSocket(plate, bx, 4, bw, bh, tint);
    const face = this.portrait(who.portrait, bw - 4, bh - 4);
    if (face !== null) {
      face.position.set(bx + 2, 6);
      node.addChild(face);
    }
    const lip = new Graphics();
    paintPortraitLip(lip, bx, 4, bw, bh);
    node.addChild(lip);
    // The faction's arms, pinned to the **outer** bottom corner of the aperture's
    // frame, the way a pennant hangs off the corner of a picture rather than
    // over its subject. It used to be laid at `bx + bw - sw + 2` - eleven of its
    // thirteen columns inside the opening, over 27% of the painted face, which
    // on a portrait cropped to a head is the head. It overlaps four columns of
    // gilt frame now and no paint. The charge is the faction's, not the side's,
    // so the same army carries the same shield in every battle.
    const sw = 11, sh = 14;
    const sx = side === 0 ? bx + bw - 4 : bx - sw + 4;
    const banner = new Graphics();
    paintStandard(banner, sw, sh, tint, chargeFor(who.faction));
    banner.position.set(sx, 4 + bh - sh + 1);
    node.addChild(banner);

    const tx = side === 0 ? bx + bw + 10 : 8;
    const tw = w - bw - 22;

    // ---- who this is
    const label = new BitmapLabel(
      { fill: COLOR.giltLit, face: "roman", scale: 2, deviceScale: this.deviceScale },
      who.name,
    );
    // Centred over the text column, not flush with its left edge. The name is
    // 5-8 glyphs in a column sized for four sockets and a mana meter, so
    // left-aligned it left a third to a half of the plate as bare leather to
    // the right of it - measured by a critic at "roughly 40% of each 570-px
    // hero plate is dead brown space". Centred, the same air reads as a margin.
    label.setAnchor(0.5, 0);
    label.position.set(Math.round(tx + (w - bw - 22) / 2), 2);
    node.addChild(label);
    // No ATTACKER/DEFENDER caption. It was a small-caps plaque label beside the
    // name, and two blind reviews in a row led with it - "console wording
    // 'ATTACKER'/'DEFENDER' ... labels the clone instantly" (2026-09-04),
    // "'ATTACKER/DEFENDER' hero plates in the console" (2026-09-07). The classic
    // names neither side anywhere on the battle screen: which army is whose is
    // said by where the hero stands and by the colour of his standard, and the
    // standard is drawn on this plaque three lines above (`paintStandard`), in
    // the owner's colour with his faction's charge on it. The plate keeps its
    // portrait, name and primaries, which is a hero card; the caption was the
    // only thing on it that was a status readout.

    // ---- one row: the four primaries in their sockets, then the mana meter.
    //
    // Two rows do not fit and were never fitting. `HERO_H` is 36 and the frame
    // `paintPlaque` draws leaves y 2..34, which the name row (1..17) and one
    // socket row (18..32) already use up; the meter was laid at `ph - 14`, i.e.
    // 22..32, straight through all four sockets. Measured on the shipped
    // 1920x1080 battle shot before this changed: the blue fill ran across every
    // one of the four devices and the word MANA sat on top of the first.
    //
    // So the row is cut to its own contents instead of to an even quarter. Each
    // cell is exactly as wide as its socket plus its own numeral, and the meter
    // takes whatever the four of them leave. The word MANA goes with it - a blue
    // trough beside three gilt devices, with a blue-white reading on its right,
    // is not an anonymous strip, and there is no room to say it twice.
    const stats: readonly (readonly [string, number])[] = [
      ["ui.primary.attack", h.attack],
      ["ui.primary.defense", h.defense],
      ["ui.primary.spell_power", h.spellPower],
      ["ui.primary.knowledge", h.knowledge],
    ];
    // The `text` face, not the `hud` face: the socket beside these digits is 14
    // arena pixels (21 device) and a 7 device pixel numeral in a 21 pixel cell is
    // a reading the plaque does not actually give. `text` is 9. It cannot be the
    // next size up - `snapMagnification` jumps 9 to 27 at this arena scale, and
    // four 27px readings plus four sockets plus a mana line do not fit 138
    // pixels of column.
    // Gold, not parchment. A critic read the plate as carrying "two type
    // families inside one plate" - a large light gold display name over a stat
    // row in plain white pixel digits with no gold and no relation to it - and
    // the fix is the cheap half of that: one family, one colour, two sizes.
    const values = stats.map(([, value]) => new BitmapLabel(
      { fill: COLOR.giltLit, face: "roman", deviceScale: this.deviceScale }, String(value),
    ));

    const manaMax = Math.max(1, h.knowledge * 10);
    const frac = Math.max(0, Math.min(1, h.mana / manaMax));
    // The reading sits **beside** the meter, not inside it. 9px light type
    // printed over a 0x2f56ad fill and a 0x0d0a06 empty track changes contrast
    // halfway through itself, and at 1:1 the number was simply not legible
    // either side of the fill line. The meter shows the proportion; the digits
    // state the value, and the digits are the half that is never dropped.
    const manaText = new BitmapLabel(
      { fill: 0xdce6ff, face: "roman", deviceScale: this.deviceScale }, `${h.mana}/${manaMax}`,
    );

    const row = plaqueStatsRow(tw, values.map((t) => t.contentWidth), manaText.contentWidth);
    for (let i = 0; i < stats.length; i++) {
      const [icon] = stats[i]!;
      const cell = row.cells[i]!;
      paintIconSocket(plate, tx + cell.rect.x, cell.rect.y, row.sock, PRIMARY_FLOOR[icon]);
      if (hasKey(this.atlas, icon)) {
        const sp = new Sprite(this.atlas.texture(icon));
        sp.anchor.set(0, 0);
        sp.width = row.sock - 4;
        sp.height = row.sock - 4;
        sp.position.set(tx + cell.rect.x + 2, cell.rect.y + 2);
        // A plain Sprite, drawn normally. The icon sheet carries real alpha -
        // `ui.primary.attack` is 6211 of its 9216 texels fully clear, and across
        // all 293 icon frames there is not one partially transparent texel - so
        // the cut is binary and the socket floor shows through of its own accord.
        //
        // This used to be drawn in the `screen` blend, because the packer
        // declared these jobs `alpha: "opaque"` and flattened every transparent
        // texel to black before packing. Screen got the ground right by accident
        // and the art wrong everywhere else: it lifts the whole frame against
        // the floor it is drawn on. Measured on this icon at 1920x1080, the
        // dodge moved the rendered 13x13 box by a mean of 13.0/255 per channel
        // (max 42) and lifted its darkest pixel from luma 0 to 33. Do not put it
        // back; if a frame ever looks like it needs it, the sheet is broken.
        node.addChild(sp);
      }
      const t = values[i]!;
      t.setAnchor(0, 0.5);
      t.position.set(tx + cell.textX, cell.rect.y + Math.round(row.sock / 2));
      node.addChild(t);
    }
    if (row.meter !== null) {
      paintMeter(plate, tx + row.meter.x, row.meter.y, row.meter.w, row.meter.h, frac, 0x3a6ad4);
    }
    manaText.setAnchor(1, 0.5);
    manaText.position.set(tx + tw, PLAQUE_ROW_Y + Math.round(PLAQUE_SOCK / 2));
    node.addChild(manaText);
    return node;
  }

  /**
   * The commander at one end of the console: the app's answer when it gives one,
   * a faction stand-in when it does not.
   *
   * `BattleHeroState` is `{id, owner, attack, defense, spellPower, knowledge,
   * mana, spellbook, castThisRound, army}` - a `HeroId` like `hero:1` and no def
   * id - so the battle state cannot name its own commander. **The app must pass
   * `UiState.commanders`**; it is the only party that holds the `GameState` the
   * `HeroId` indexes, and `AdventureShell` already resolves the same id to a
   * portrait key for the after-action report.
   *
   * Until it does, the console still has to put a face at the end of the bar, so
   * it picks one deterministically from the army's own faction roster, matched
   * on might against magic so a hero with 9/7 primaries is a Knight and one with
   * 5/6 is a Bonecaller. It is a **stand-in, not a lookup**: the face and the
   * name are the faction's, not this particular hero's.
   */
  private commanderOf(
    state: BattleState, side: 0 | 1,
  ): { readonly name: string; readonly portrait: string | null; readonly faction: string } {
    const faction = this.armyFaction(state, side);
    const supplied = this.lastUi.commanders?.[side] ?? null;
    if (supplied !== null) return { name: supplied.name, portrait: supplied.portrait, faction };

    const h = state.heroes[side];
    const might = h.attack + h.defense >= h.spellPower + h.knowledge;
    // Sorted by id, so the same battle picks the same commander on every frame
    // and on every machine.
    const roster: { readonly id: string; readonly name: string; readonly art: string }[] = [];
    for (const def of this.content.heroes.values()) {
      if ((def.faction as string) !== faction) continue;
      if (def.might !== might) continue;
      roster.push({ id: def.id as string, name: def.name, art: def.portrait });
    }
    roster.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    if (roster.length === 0) {
      return { name: this.armyName(state, side), portrait: null, faction };
    }
    const key = (h.id as string | null) ?? `side:${side}`;
    const pick = roster[hashKey(key) % roster.length]!;
    return { name: pick.name, portrait: portraitKeyOf(pick.art), faction };
  }

  /** A painted portrait, fitted to a box and masked to it. */
  private portrait(key: string | null, w: number, h: number): Container | null {
    if (key === null || !hasKey(this.atlas, key)) return null;
    const frame = this.atlas.frame(key);
    const box = new Container();
    const s = new Sprite(this.atlas.texture(key));
    const srcW = Math.max(1, frame?.w ?? 96);
    const srcH = Math.max(1, frame?.h ?? 96);
    // Cover, not contain: a portrait letterboxed inside its own aperture reads
    // as a picture of a picture. The head sits high in every one of the 199, so
    // the crop is taken off the bottom.
    const scale = Math.max(w / srcW, h / srcH);
    s.anchor.set(0.5, 0);
    s.scale.set(scale);
    s.position.set(w / 2, 0);
    const mask = new Graphics();
    mask.poly(chamfered(0, 0, w, h, CHAMFER - 2)).fill({ color: 0xffffff });
    box.addChild(s, mask);
    box.mask = mask;
    return box;
  }

  /** The faction most of a side's stacks belong to; "" when it has none. */
  private armyFaction(state: BattleState, side: 0 | 1): string {
    const st = state.stacks;
    const tally = new Map<string, number>();
    for (let i = 0; i < st.n; i++) {
      if (st.side[i] !== side) continue;
      const def = this.content.creatures.get(st.def[i]!);
      if (def === undefined) continue;
      const f = def.faction as string;
      tally.set(f, (tally.get(f) ?? 0) + 1);
    }
    let best = "";
    let bestN = 0;
    // Sorted, so a tie resolves the same way on every frame and every machine.
    for (const f of [...tally.keys()].sort()) {
      const n = tally.get(f)!;
      if (n > bestN) { bestN = n; best = f; }
    }
    return best;
  }

  /** The faction the side's stacks come from, as a name a player recognises. */
  private armyName(state: BattleState, side: 0 | 1): string {
    const best = this.armyFaction(state, side);
    if (best === "") return side === 0 ? "Attacking army" : "Defending army";
    const def = this.content.factions.get(best as never);
    return def?.name ?? best.charAt(0).toUpperCase() + best.slice(1);
  }


  // ---------------------------------------------------------------- paint

  /**
   * Where the active stack may go: the ground **outside** the reach multiplied
   * down, and the reach itself left in the board's own light.
   *
   * The operator was settled two rounds ago and is not what moved. Three
   * versions lit their region instead, the third of them measured at +21.8 luma
   * over a backdrop whose own tile means span 111.6, and an additive lift is the
   * wrong operator whatever its alpha is: the quantity it adds is fixed and the
   * thing it competes with - the painting's own light and shade - is not. A
   * multiply is a fixed *ratio*, so the marking is as legible in the bright
   * middle of the field as in the shaded corners. *How much* of a ratio is
   * `reachShadeAlpha`'s job.
   *
   * Which side of the boundary takes it is the classic's side, the reach itself; see
   * `paintReachRegion`.
   */
  private paintReach(state: BattleState, ui: UiState): void {
    const g = this.reach;
    g.clear();
    // The cells the living stand on are not ground to walk to, and the classic's
    // marking leaves them in the light; shaded, a stack's own feet were the
    // darkest part of its cell.
    const occupied = new Set<number>();
    for (let i = 0; i < state.stacks.n; i++) {
      if ((state.stacks.flags[i]! & F_ALIVE) === 0 || state.stacks.count[i]! <= 0) continue;
      for (const h of stackFootprintOf(state, i).hexes) occupied.add(h as number);
    }
    // Only while the pointer is on the board, or the lattice is up. The classic's
    // default shows no movement shadow at all, and the expansion's is a per-hex shade
    // the player switches on; a speed-12 High Radiant reaches most of a board,
    // and drawn at rest that was one dark shape over 62% of the field with a
    // hex-serrated outline - the largest single thing on the screen, and not
    // one the references draw. Under the pointer it is the answer to "where
    // can I go", which is the only time the question is being asked.
    if (ui.showGrid === true || ui.showReach === true) {
      paintReachRegion(g, ui.reachable.filter((h) => !occupied.has(h)), state.terrain);
    }
    // The stand-from cells stay a *lit* region, and now read as one against the
    // shaded reach they sit inside rather than as a slightly warmer patch of it.
    paintRegion(g, ui.attackFrom, COLOR.attack, 0.32, 0xffd9c0, 0.85);
    // Last, so the lattice runs unbroken over the shading and over the lit
    // stand-from patch alike. Its whole job is to be the same line on both sides
    // of every region boundary on the board. Only when asked for: see
    // `UiState.showGrid`.
    if (ui.showGrid === true) paintBoardGrid(g, state.terrain);
    paintHazards(g, state, ui);
  }

  /**
   * The cells a stack stands on, read the same way `stacks.ts` reads them.
   *
   * The selection hex used to be `hexPolygon(state.stacks.hex[active])` - the
   * *head* cell - while the sprite was drawn from the midpoint of a two-hex
   * stack's two cells. On a Sunscale Wyrm that put the gold hex half a cell up
   * and left of the creature it was marking: only its front claws were inside
   * the hex, and the hex's other half was empty grass. A marker and a sprite
   * that read the grid through two expressions drift; there is one expression
   * now and both call it.
   */
  private footprintOf(state: BattleState, slot: number): StackFootprint {
    return stackFootprintOf(state, slot);
  }

  private paintRings(state: BattleState, ui: UiState): void {
    const g = this.rings;
    g.clear();
    if (ui.showActiveMarker === true && ui.activeStack >= 0 && ui.activeStack < state.stacks.n) {
      // The stack whose turn it is. A 1px gold hairline was indistinguishable
      // from the reach edging at full-screen scale, and with a neighbour drawn
      // in front of it the player's only cue was the initiative bar 500px away.
      const foot = this.footprintOf(state, ui.activeStack);
      for (const poly of markerPolygons(foot)) g.poly([...poly], true);
      g.fill({ color: COLOR.giltLit, alpha: 0.10 });
      // The footprint's own outline, so a two-hex stack is ringed once rather
      // than twice with a seam down the middle of the creature.
      //
      // Every pass is drawn *inside* the outline. With the gilt at `alignment:
      // 1` the ring stood 2.2px proud of the cell on every side, so a marker a
      // critic measured came out 97 device px across a 90px cell and 187 across
      // two - it read as covering ground the stack does not own. Inside, the
      // marker's painted bounding box is the cell's bounding box, which is the
      // invariant `battle-marker.spec.ts` asserts, and the three widths still
      // stack into a bevel: dark rim outermost, gilt, lit gold innermost.
      //
      // Quieter than it was (3.5 / 2.2 / 1 at up to full alpha): the classic marks the
      // acting stack on the ground with nothing louder than a lit hex, and a
      // three-band gilt bevel round one cell was the brightest edge on the
      // board.
      const edges = markerEdges(foot);
      for (const [w, c, a] of [
        [2.5, 0x1c1204, 0.5], [1.5, COLOR.gilt, 0.9], [0.8, COLOR.giltLit, 0.7],
      ] as const) {
        for (const e of edges) g.moveTo(e[0]!, e[1]!).lineTo(e[2]!, e[3]!);
        g.stroke({ width: w, color: c, alpha: a, alignment: 0 });
      }
    }
    // The hovered cell: ONE single-tone hairline inside the outline, the expansion's
    // optional light outline and nothing more. It was a 2px dark line under a
    // 1px lit one plus a second outline and a chevron from `paintCursor`, and
    // a critic measured "a ~4px two-tone hexagon with a triangle in it". With
    // the grid off the classic itself paints nothing here - the cursor changes.
    if (ui.hoverHex !== null && ui.hoverStack === null) {
      g.poly([...hexPolygon(asHex(ui.hoverHex))], true)
        .stroke({ width: 1, color: COLOR.reachEdge, alpha: 0.7, alignment: 0 });
    }
    if (ui.hoverStack !== null && ui.hoverStack < state.stacks.n) {
      const edges = markerEdges(this.footprintOf(state, ui.hoverStack));
      for (const e of edges) g.moveTo(e[0]!, e[1]!).lineTo(e[2]!, e[3]!);
      g.stroke({ width: 1, color: COLOR.attack, alpha: 0.8, alignment: 0 });
    }
  }

  /**
   * What a click will do, drawn on the field: the walk the pointer is asking
   * for, or the hex the attacker will strike from and the direction it strikes.
   *
   * The classic answers "what happens if I click here" on the board itself - a chain
   * of arrows down the path and a glyph on the destination saying what it is -
   * and nothing on this screen did. `ui.path` comes from the app's own call to
   * the kernel's pathfinder; when it is absent only the destination is marked,
   * because a straight line drawn between two hexes is a route the rules may
   * not take.
   */
  private paintCursor(ui: UiState): void {
    const g = this.cursor;
    g.clear();
    g.visible = false;
    if (ui.pointer === null || ui.hoverHex === null) return;

    // ---- attacking: the stand hex, and a blade pointing at the target
    if (ui.hoverStack !== null && ui.attackFrom.length > 0) {
      const pick = attackApproach(asHex(ui.hoverHex), px(ui.pointer.x, ui.pointer.y), ui.attackFrom);
      if (pick === null) return;
      g.visible = true;
      const from = hexCentre(pick.hex);
      const to = hexCentre(asHex(ui.hoverHex));
      g.poly([...hexPolygon(pick.hex)], true).fill({ color: COLOR.attack, alpha: 0.3 });
      g.poly([...hexPolygon(pick.hex)], true).stroke({ width: 2, color: 0xffd9c0, alpha: 0.95, alignment: 1 });
      this.paintPath(g, ui.path ?? [], pick.hex as number);
      const angle = Math.atan2(to.y - from.y, to.x - from.x);
      const mx = from.x + (to.x - from.x) * 0.42;
      const my = from.y + (to.y - from.y) * 0.42;
      const cos = Math.cos(angle), sin = Math.sin(angle);
      const pt = (lx: number, ly: number): readonly [number, number] =>
        [mx + lx * cos - ly * sin, my + lx * sin + ly * cos];
      const poly: number[] = [];
      for (const [lx, ly] of [[16, 0], [2, -9], [2, -3], [-16, -3], [-16, 3], [2, 3], [2, 9]] as const) {
        const [ax, ay] = pt(lx, ly);
        poly.push(ax, ay);
      }
      g.poly(poly, true).fill({ color: 0xffe0c4 });
      g.poly(poly, true).stroke({ width: 1.5, color: 0x511a0a, alpha: 0.9, alignment: 0.5 });
      return;
    }

    // ---- walking: the path only. The destination cell already carries the
    // hover hairline (`paintRings`); the second, heavier outline and the
    // chevron that used to be stamped here were the "thick two-tone hexagon
    // with a triangle inside" a critic named. The classic marks the destination
    // with the cursor.
    if (!ui.reachable.includes(ui.hoverHex)) return;
    g.visible = true;
    this.paintPath(g, ui.path ?? [], ui.hoverHex);
  }

  /** A chain of arrow heads down the kernel's own path, ending at `to`. */
  private paintPath(g: Graphics, path: readonly number[], to: number): void {
    if (path.length < 2) return;
    for (let i = 1; i < path.length; i++) {
      const a = hexCentre(asHex(path[i - 1]!));
      const b = hexCentre(asHex(path[i]!));
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const cos = Math.cos(ang), sin = Math.sin(ang);
      const head: number[] = [];
      for (const [lx, ly] of [[7, 0], [-5, -6], [-2, 0], [-5, 6]] as const) {
        head.push(mx + lx * cos - ly * sin, my + lx * sin + ly * cos);
      }
      g.poly(head, true).fill({ color: 0x2b1a06, alpha: 0.7 });
      const lit: number[] = [];
      for (const [lx, ly] of [[5.5, 0], [-4, -4.6], [-1.5, 0], [-4, 4.6]] as const) {
        lit.push(mx + lx * cos - ly * sin, my + lx * sin + ly * cos);
      }
      g.poly(lit, true).fill({ color: COLOR.reachEdge, alpha: 0.95 });
    }
    void to;
  }

  // --------------------------------------------------------- initiative

  /**
   * Turn order for the round, from the stack acting now. `state.order` is the
   * kernel's own array, so a WAIT that reshuffles the queue shows up here for
   * free - which is the whole reason the rail is worth drawing.
   *
   * It runs **down the right margin**, not across the top. Three shapes have
   * been tried: a 1015px panel floating at y=8 over the battlefield with its own
   * drop shadow, cut off in mid-air at both ends and occluding the mountain
   * range across 53% of the frame; then a docked full-width band, which fixed
   * the HUD-island reading and cost 44px of the frame's height; and now a
   * vertical rail, which costs none. The expansion HD does the same thing for the same
   * reason - a 16:9 frame has width to spare and no height - and the five
   * reference frames between them spend 6.7-7.2% of their height on chrome
   * against our 15.6% with the band. See `TOPBAR_H` in `grid.ts`.
   *
   * The commanders come with it, into the two top corners of the frame, which
   * is where the expansion HD keeps its own hero panel.
   */
  /** Compact chrome: the console is cropped away and so is the sky. */
  private consoleHidden = false;

  private paintInitiative(state: BattleState): void {
    for (const c of this.barSlots.removeChildren()) c.destroy({ children: true, context: true });
    const slots: number[] = [];
    for (let i = state.turnIndex; i < state.order.length && slots.length < 24; i++) {
      const v = state.order[i]!;
      if (v < 0) continue;
      if ((state.stacks.flags[v]! & F_ALIVE) === 0 || state.stacks.count[v]! <= 0) continue;
      slots.push(v);
    }
    this.bar.visible = true;
    this.bar.position.set(0, 0);

    const lay = initiativeLayout(this.view, slots.length, this.consoleHidden);
    // The strip draws as many of the queue as fit at one plate size.
    if (slots.length > lay.shown) slots.length = lay.shown;
    this.barPanel.clear();
    if (slots.length === 0) return;

    const gap = lay.gap;
    const cw = lay.cardW;
    const ch = lay.cardH;
    // The band: a dark strip with a gilt keyline, the HD queue's own furniture.
    // It is drawn into `barPanel` rather than into `barSlots` so it sits behind
    // every plate that rides in it.
    paintQueueBand(this.barPanel, lay.rail.x, lay.rail.y, lay.rail.w, lay.rail.h);
    // A truncated queue says so, at the end of the strip. Silently dropping the
    // tail would make the strip read as the whole turn order when it is not.
    if (lay.hidden > 0) {
      const more = new BitmapLabel(
        { fill: COLOR.giltLit, deviceScale: this.deviceScale }, `+${lay.hidden}`,
      );
      more.setAnchor(0.5, 0.5);
      const mcx = Math.round(lay.rail.x + lay.rail.w - lay.pad - lay.moreW / 2);
      const mcy = Math.round(lay.rail.y + lay.rail.h / 2);
      const tabW = Math.ceil(more.contentWidth) + 8;
      const tabH = Math.min(ch, Math.ceil(more.contentHeight) + 6);
      const tab = new Graphics();
      tab.poly(chamfered(mcx - tabW / 2, mcy - tabH / 2, tabW, tabH, 2))
        .fill({ color: 0x140d07 });
      tab.poly(chamfered(mcx - tabW / 2 + 1, mcy - tabH / 2 + 1, tabW - 2, tabH - 2, 2))
        .fill({ color: 0x2c1f11 });
      tab.poly(chamfered(mcx - tabW / 2, mcy - tabH / 2, tabW, tabH, 2))
        .stroke({ width: 1, color: COLOR.giltDark, alpha: 0.9, alignment: 0 });
      more.position.set(mcx, mcy);
      this.barSlots.addChild(tab, more);
    }

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i]!;
      const node = new Container();
      node.position.set(lay.x0 + i * (cw + gap), lay.y0);
      const tint = state.stacks.side[slot] === 0 ? this.colors.attacker : this.colors.defender;
      const box = queueThumb(cw, ch, 1, 1).niche;
      const plate = new Graphics();
      paintStonePlate(plate, cw, ch, tint, 11 + slot * 3);
      paintInset(plate, box.x, box.y, box.w, box.h, tint);
      node.addChild(plate);

      // **No count on the icon.** The expansion's queue carries none, and ours carried
      // one in a black tab that took a fifth of a 30x40 plate; on a square
      // plate it would take a third. The count is on the board, on the stack's
      // own plaque, which is where a player reads it.
      const thumb = this.creatureThumb(state, slot, box.w, box.h, 0);
      if (thumb !== null) {
        thumb.node.position.set(box.x, box.y);
        node.addChild(thumb.node);
      }
      if (i === 0) {
        // The stack acting now. One gilt hairline on the plate's own edge, not
        // a three-band bezel standing proud of it: the strip is chrome docked
        // to the frame and the loudest thing in it should not be louder than
        // the frame's own bead.
        const ring = new Graphics();
        ring.poly(chamfered(-1, -1, cw + 2, ch + 2, CHAMFER))
          .stroke({ width: 1, color: 0x1c1204, alpha: 0.8, alignment: 0 });
        ring.poly(chamfered(0, 0, cw, ch, CHAMFER))
          .stroke({ width: 1.5, color: COLOR.giltLit, alpha: 1, alignment: 0 });
        node.addChild(ring);
      }
      this.barSlots.addChild(node);
    }
  }

  /**
   * A queue thumbnail: the **whole** creature, fitted to the plate.
   *
   * Cropping the top of the frame put a Spearwarden's spear point in one plate and
   * an High Radiant's whole torso in the next - measured, a 3x difference in
   * subject scale between adjacent plates, and the plates for slot 4 and slot 18
   * read as empty with a speck in them. Fitting the sprite's own box to the
   * recess makes every plate the same apparent size by construction, which is
   * the only property a turn queue actually needs.
   */
  private creatureThumb(
    state: BattleState, slot: number, w: number, h: number, reserve: number,
  ): { readonly node: Container; readonly box: Rect } | null {
    const def = this.content.creatures.get(state.stacks.def[slot]!);
    if (def === undefined) return null;
    const ref = creatureArtRef(def.art);
    const key = ref.kind === "sheet" ? unitFrameKey(ref, "front", POSE.idle) : ref.key;
    if (!hasKey(this.atlas, key)) return null;
    const frame = this.atlas.frame(key);
    const box = new Container();
    const s = new Sprite(this.atlas.texture(key));
    const srcW = Math.max(1, frame?.w ?? 150);
    const srcH = Math.max(1, frame?.h ?? 290);
    // `w`/`h` are the niche, so the plate it was cut from is 8 larger both ways.
    const fit = queueThumb(w + 8, h + 8, srcW, srcH);
    const floorY = fit.floorY;
    const scale = fit.scale;
    // Left of centre, so the count tab in the bottom-right corner clears the
    // creature - but never so far that a wide, short frame (the packed sheets
    // run to 295x124) hangs out of its own niche and gets clipped by the mask.
    const half = fit.drawnW / 2;
    // `reserve` is the column the count tab claims at the right of the niche:
    // the creature stands clear of it when its own width leaves room, and stays
    // centred when it does not - it is never pushed out of its own niche to make
    // room, because a masked creature is worse than a tab on a cloak hem.
    const cx = Math.round(
      fit.drawnW >= w
        ? w / 2
        : Math.min(Math.max(w * CARD_THUMB_CENTRE, half), Math.max(half, w - reserve - half)),
    );
    // A pool of shade under the feet, the same stepped shadow the board uses:
    // without it a fitted sprite floats in the niche.
    const shade = new Graphics();
    const rx = Math.max(4, srcW * scale * 0.44);
    shade.ellipse(cx, floorY - 1, rx, Math.max(1.6, rx * 0.24))
      .fill({ color: 0x08060a, alpha: 0.42 });
    shade.ellipse(cx, floorY - 1, rx * 0.6, Math.max(1.2, rx * 0.15))
      .fill({ color: 0x050406, alpha: 0.4 });
    s.anchor.set(0.5, 1);
    s.scale.set(state.stacks.side[slot] === 0 ? scale : -scale, scale);
    s.position.set(cx, floorY);
    const mask = new Graphics();
    mask.rect(0, 0, w, h).fill({ color: 0xffffff });
    box.addChild(shade, s, mask);
    box.mask = mask;
    return {
      node: box,
      box: {
        x: cx - fit.drawnW / 2, y: floorY - fit.drawnH,
        w: fit.drawnW, h: fit.drawnH,
      },
    };
  }

  // ---------------------------------------------------------- info panel

  private paintInfo(state: BattleState, ui: UiState): void {
    const slot = ui.inspectStack ?? null;
    if (slot === null || ui.pointer === null || slot >= state.stacks.n) {
      this.info.visible = false;
      return;
    }
    const def = this.content.creatures.get(state.stacks.def[slot]!);
    if (def === undefined) { this.info.visible = false; return; }
    this.info.visible = true;
    for (const c of this.infoBody.removeChildren()) c.destroy({ children: true, context: true });

    const st = state.stacks;

    // Every string on this panel is a bitmap label. It was six `Text` nodes in
    // Georgia at 11 to 15px, drawn through the arena's 1.5x magnification, so
    // the hovered-stack panel carried a hinted serif over a painted plate - the
    // same tell the console line carried. The three faces are cap 11, 9 and 7;
    // the panel uses `romanDisplay` for the name and `roman` for the rest -
    // the roman family, because every panel of the commercial game is a serif.
    const scale = this.deviceScale;
    const title = new BitmapLabel(
      { fill: COLOR.giltLit, face: "romanDisplay", deviceScale: scale },
      st.count[slot]! === 1 ? def.name : def.namePlural,
    );
    const count = new BitmapLabel(
      { fill: 0xd6c39a, face: "roman", deviceScale: scale }, `${st.count[slot]!} in stack`,
    );

    const rows: readonly (readonly [string, string, string])[] = [
      ["ui.primary.attack", "Attack", String(st.attack[slot]!)],
      ["ui.primary.defense", "Defense", String(st.defense[slot]!)],
      ["", "Damage", `${st.damageMin[slot]!} - ${st.damageMax[slot]!}`],
      ["", "Health", `${st.topHp[slot]!} / ${st.maxHp[slot]!}`],
      ["", "Speed", String(st.speed[slot]!)],
      ["ui.mood.morale_high", "Morale", signed(st.morale[slot]!)],
      ["ui.mood.luck_high", "Luck", signed(st.luck[slot]!)],
    ];
    const shots = st.shots[slot]! > 0 ? `${st.shots[slot]!} shots left` : "";

    // The panel is cut to its contents rather than to a constant. A bitmap line
    // reserves its face's whole descent band, so a fixed 176 that fitted the
    // vector setting would have put the last row through the frame's bottom
    // bevel; the height is measured off the same numbers the rows are laid at.
    const w = 214;
    const headY = 10;
    const countY = headY + Math.ceil(title.contentHeight) + 4;
    const ruleY = countY + Math.ceil(count.contentHeight) + 6;
    const rowH = 16;
    const bodyY = ruleY + 7;
    const h = bodyY + rows.length * rowH + (shots === "" ? 0 : rowH) + 6;
    paintPanel(this.infoPanel, w, h);
    // A hairline under the name, the way every plate on this screen separates a
    // heading from its figures: dark groove, lit lip under it, one light source.
    this.infoPanel.rect(12, ruleY, w - 24, 1).fill({ color: 0x1a1109, alpha: 0.85 });
    this.infoPanel.rect(12, ruleY + 1, w - 24, 1).fill({ color: COLOR.woodLit, alpha: 0.45 });

    title.position.set(12, headY);
    count.position.set(12, countY);
    this.infoBody.addChild(title, count);

    let y = bodyY;
    for (const [icon, label, value] of rows) {
      if (icon !== "" && hasKey(this.atlas, icon)) {
        const s = new Sprite(this.atlas.texture(icon));
        s.anchor.set(0, 0.5);
        s.width = 13;
        s.height = 13;
        s.position.set(12, y + 5);
        this.infoBody.addChild(s);
      }
      const l = new BitmapLabel({ fill: 0xc8b48a, face: "roman", deviceScale: scale }, label);
      l.position.set(30, y);
      const v = new BitmapLabel({ fill: COLOR.parchment, face: "roman", deviceScale: scale }, value);
      v.setAnchor(1, 0);
      v.position.set(w - 12, y);
      this.infoBody.addChild(l, v);
      y += rowH;
    }
    if (shots !== "") {
      const s = new BitmapLabel({ fill: 0xbfae86, face: "roman", deviceScale: scale }, shots);
      s.position.set(12, y);
      this.infoBody.addChild(s);
    }

    // Clamp to the visible window so the panel never runs off the edge, and
    // under the turn strip, which lies across the top of the field: a panel
    // that slid up under it covered the queue.
    const v = this.view;
    const right = v.x + v.w - w - 8;
    const x = Math.min(right, Math.max(v.x + 8, ui.pointer.x + 20));
    const strip = initiativeLayout(v, 1).rail;
    const top = strip.y + strip.h + 8;
    const py = Math.min(v.y + v.h - BAND_H - h - 8, Math.max(top, ui.pointer.y - h - 16));
    this.info.position.set(Math.round(x), Math.round(py));
  }
}

/**
 * The turn strip's band: dark planking with a gilt keyline, cut with the same
 * chamfer as the console's plates so the two read as one set of furniture.
 * Quiet on purpose - it lies over the painted sky, and a bright bar there is a
 * second horizon.
 */
/**
 * The trough the queue rides in: a flat run of the frame's own hide, closed by
 * one gilt bead along its foot.
 *
 * It was a chamfered panel with a double keyline, a lit top edge and a dark
 * bottom edge - furniture that reads as an object laid on the picture. It runs
 * edge to edge inside the leather now, so its ends are the frame and it needs
 * no edge of its own; the one line it does need is the bead where it meets the
 * sky, which is the same bead the frame closes with.
 */
function paintQueueBand(g: Graphics, x: number, y: number, w: number, h: number): void {
  g.rect(x, y, w, h).fill({ color: 0x241a0f, alpha: 0.97 });
  grain(g, x, y, w, h, 61, 0.5);
  // The rail's own shadow falling into the trough, so the strip sits under the
  // frame rather than in front of it.
  g.rect(x, y, w, 2).fill({ color: 0x000000, alpha: 0.4 });
  g.rect(x, y + h - 2, w, 1).fill({ color: COLOR.giltDark, alpha: 0.85 });
  g.rect(x, y + h - 1, w, 1).fill({ color: 0x000000, alpha: 0.5 });
}

/**
 * A set of cells as one shape: the union filled in a single non-zero-wound
 * path, then a dark lip and a lit hairline traced along the union's boundary
 * only. Nothing is drawn on an edge two marked cells share, which is what keeps
 * a hundred marked cells from reading as a hundred hexagons.
 */
function paintRegion(
  g: Graphics, hexes: readonly number[], fill: number, fillAlpha: number,
  edge: number, edgeAlpha: number,
): void {
  if (hexes.length === 0) return;
  for (const h of hexes) g.poly([...hexPolygon(asHex(h))], true);
  g.fill({ color: fill, alpha: fillAlpha });
  const edges = regionEdges(hexes);
  for (const e of edges) g.moveTo(e[0]!, e[1]!).lineTo(e[2]!, e[3]!);
  g.stroke({ width: 2.5, color: 0x17100a, alpha: edgeAlpha * 0.5 });
  for (const e of edges) g.moveTo(e[0]!, e[1]!).lineTo(e[2]!, e[3]!);
  g.stroke({ width: 1, color: edge, alpha: edgeAlpha });
}

/**
 * The colour the reach region is filled with: **black**, so the fill is a
 * multiply and nothing else.
 *
 * It was `0x0d0a06` - "near-black rather than black: the shade keeps a trace of
 * the warm ground" - and near-black is not a multiply, it is a multiply plus a
 * fixed lift of `alpha * 10.3`. That is invisible on a bright arena and is most
 * of the operator on a dark one. Fitted over the four photographed boards, the
 * shipped marking was `shaded = 0.55 * open + 4.63`, within 1.4 luma on all
 * four; on lava, whose open board is at luma 29.1, the lift handed back a third
 * of the drop the multiply was asking for and the marking measured 8.9 luma deep
 * instead of the 13.1 a true 0.55 would have given.
 *
 * The classic's is a true multiply. Measured on a reference screenshot - the publisher's own
 * classic gallery montage, bottom row second panel, a siege with the hex grid and
 * the darkened region on screen together - the marked ground's median RGB is
 * (41,44,12) against (77,78,21) beside it: per-channel ratios 0.532 / 0.564 /
 * 0.571, one ratio on all three channels and no offset on any of them.
 */
export const REACH_SHADE = 0x000000;

/**
 * How deep the reach marking cuts, in luma levels, and why it is a depth rather
 * than a fraction.
 *
 * A fraction is what this was - one alpha for all nineteen arenas - and a
 * fraction is the right operator only while every board sits at about the same
 * brightness. Ours do not, and stopped pretending to when `GROUND_TONES` gave
 * each arena its own plate's colours: measured by the method in
 * `battle-grid.spec.ts` on the 1920x1080 shots, the open board runs
 *
 *   lava 29.1   cavern 56.0   dirt 94.5   snow 214.0
 *
 * and one multiply over that range is four different markings. At 0.45 it was
 * worth 8.9 luma on lava and 91.5 on snow - in CIE L*, a step of 4.4 against one
 * of 34.2. On lava that is at the edge of visible; on snow it is a flat grey
 * slab over two thirds of the board.
 *
 * So the constant is the depth, and the alpha is solved per arena from
 * `groundLevel`. **36 is the classic's own.** Two samplings of the reference frame
 * named on `REACH_SHADE`: three clean grass patches inside the darkened region
 * against four outside it give medians 40.84 and 73.68, a drop of 32.8 and a
 * multiply of 0.554; a tighter pair of patches chosen to be the same grass in
 * both states gives 40.3 and 79.5, a drop of 39.2 and a multiply of 0.507. 36 is
 * the middle of that, and it lands our dirt board - the only arena whose ground,
 * lattice and creature exposure have all been checked against a classic anchor -
 * within 2 luma of where it already was.
 */
export const REACH_DROP = 36;

/**
 * The most of the ground the marking may take: 60%, i.e. never below 0.40 of it.
 *
 * On the two darkest arenas `REACH_DROP` cannot be paid - lava's whole board is
 * 29.1 luma deep - so the depth clamps to a fraction, and this is that fraction.
 * It is set against the classic's own marking, which leaves 0.51 to 0.55 of its
 * ground: the arenas that cannot afford the reference's depth are allowed a
 * fifth more strength than the reference uses, and no more.
 *
 * Where it sits on the curve *is* measured, and the curve has no knee to hide
 * behind - it is very nearly linear in the multiply, which is worth saying
 * because "diminishing returns" is the argument this number invites and the
 * numbers do not support it. On lava's board, in CIE L*, the marking is worth
 *
 *   0.60 -> 5.6    0.55 -> 6.1    0.50 -> 6.7    0.45 -> 7.2
 *   0.40 -> 7.6    0.30 -> 8.4    0.20 -> 9.2    0.00 -> 10.8
 *
 * so 0.40 buys 70% of everything a subtractive marking can ever get out of that
 * plate, and going twice as hard would buy 85%. The stopping point is a
 * judgement about what else the board has to do - at 0.40 the plate keeps 40% of
 * its own contrast and its lava cracks are still legible inside the region - and
 * it is written here as a judgement. What is not a judgement is the ceiling on
 * the other side: 10.8 is all there is, against the classic's 17.6, so lava's marking
 * is short because lava's board is 29 luma deep and no operator fixes that.
 *
 * Lava and cavern are the only two arenas that reach the clamp.
 */
export const REACH_MULT_FLOOR = 0.4;

/**
 * The alpha the reach region is filled at on a given arena: `REACH_DROP` levels
 * of that arena's own open board, clamped by `REACH_MULT_FLOOR`.
 *
 * The alpha is derived from the plate; the rest of this table is what the four
 * photographed boards then measure, by the method in `battle-grid.spec.ts`:
 *
 *            open   alpha    drop      dL*         was
 *   lava      29.1   0.60    17.9     7.74     8.9 /  4.40
 *   cavern    56.0   0.60    34.6    16.57    21.9 / 10.25
 *   dirt      94.5   0.39    37.3    16.06    38.5 / 16.59
 *   snow     214.0   0.17    35.8    12.99    91.5 / 34.20
 *
 * against the classic's own 17.6 L*. Three of the four now sit within a quarter of the
 * reference where they spanned a factor of eight, and dirt - the board that was
 * already right - moves by half an L* step. Lava is short because its board is
 * 29 luma deep: 10.8 L* is everything a multiply can take out of it even at pure
 * black, so that is the plate's ceiling and not the operator's.
 */
export function reachShadeAlpha(terrain: string): number {
  const want = REACH_DROP / Math.max(1, groundLevel(terrain));
  return Math.min(1 - REACH_MULT_FLOOR, want);
}

/**
 * The alpha on the arena everything else on this screen is calibrated to, kept
 * as a constant so a test can state the operator without naming a terrain.
 */
export const REACH_SHADE_ALPHA = reachShadeAlpha("dirt");
/**
 * What the per-cell hex outline used to be blended at, and the reason it is not
 * blended at anything any more.
 *
 * The reasoning was: draw the pale chrome cream over the shade at the strength
 * that puts the line back at roughly the unshaded ground's own brightness, so
 * it reads as the field showing through the shading rather than as a line added
 * to it. On a ground of luma 124 shaded to 68, `0.68*68 + 0.32*236` comes to
 * 122, and that part checked out - measured on the shipped frame the line ran
 * +52.2 luma over the shaded ground.
 *
 * What it cannot do is survive being drawn over anything else. An alpha-blended
 * line's value is `(1-a)*ground + a*colour`, so its *contrast* is `a*(colour -
 * ground)`: it thins out as the ground rises toward the line's own colour, and
 * on the unshaded half of this field the same cream would have been worth only
 * +36. The classic's grid is opaque for exactly this reason (`GRID_LINE_COLOR`), and the
 * board lattice is now drawn that way and covers these cells among all the rest.
 *
 * The constant stays because `battle-reach.spec.ts` reads the marking's shape
 * off it and because the ratio it has to keep - quieter than the shade it sits
 * on - is still the rule any replacement has to obey.
 */
export const REACH_CELL_ALPHA = 0.32;

/** Rec.709 luma of a packed 0xRRGGBB. */
const luma = (c: number): number =>
  0.2126 * ((c >> 16) & 255) + 0.7152 * ((c >> 8) & 255) + 0.0722 * (c & 255);

/**
 * How far the lattice stands off the ground it is drawn on, in luma: **47**,
 * which is the reference's own figure over its own open ground.
 *
 * **What was here and why it was too much.** The lift was `luma(0xc0c094) -
 * groundLevel("dirt")` = **95.7** - dirt's own figure, kept because
 * `battle-grid.spec.ts` pins a line at 168.9 luma or brighter and the reference
 * does not reach it - and it was recorded as a known divergence rather than
 * split. Measured on the seven shipped 1920x1080 arenas by sampling the line's
 * exact colour against the open board's own modal tone, it delivered +95.7 on
 * six boards and -95.7 on snow: on dirt the line was `(192,192,148)` at luma
 * 188.8 over a board at 93.1, and the hex web rather than the creatures was the
 * loudest graphic in the frame on four of the seven.
 *
 * **What the references measure.** On a reference screenshot, the
 * one file on disk that carries a drawn grid and a darkened region at once, the
 * line clears its open ground by **+47** and its shaded ground by **+75**. Both
 * are lower bounds - a 1px line in a 4:2:0 JPEG is measured mixed with the
 * ground beside it - but they are the only two figures anyone can re-derive, and
 * 95.7 is twice the larger of them.
 *
 * So the lift is 47, over the board's own unshaded level, and the +83 the line
 * then reaches over the shaded third falls out of `REACH_DROP` rather than being
 * chosen: 47 + 36, against the reference's own 75. The lattice's contrast is
 * therefore bounded by the reference's own pair on every arena.
 *
 * **`groundLevel` is the right anchor and this is the check.** It estimates the
 * unshaded board from the plate (`luma(mid) * GROUND_GRADE`), and the modal
 * unshaded tone of each of the seven shipped frames reads
 *
 *            estimate   shot
 *   lava        26.4     27.7   (0x171d1c)
 *   cavern      53.3     56.3   (0x692c23)
 *   wasteland   90.9     96.0   (0x5b625b)
 *   dirt        93.1     91.4   (0x705841)
 *   highlands   93.3     94.1   (0x5e6238)
 *   siege       96.4    101.4   (0x6b6b1d)
 *   snow       216.7    217.0   (0xd9d9d9)
 *
 * - good to about 5 luma over a range of 190. The board *median* is a much
 * lower number on all seven (60.1 on dirt), and it is not the one to calibrate
 * against: two thirds of the median's population is the reach marking's own
 * multiply, so a lift derived from it would be a lift derived from this file's
 * other pass.
 */
export const GRID_LINE_LIFT = 47;

/**
 * How much of the ground's own chroma the line carries: **half of it**.
 *
 * The line used to be solved as R = G = v with B = v - 44, one olive on all
 * twenty-one arenas, and the 44 was the reference line's own R-B measured on
 * `CLASSIC_Battlefield.jpg` over 3,228 line pixels. Copying that number was the
 * mistake: the reference frame is a *grass* battlefield, so its line is a
 * desaturated version of the ground it is drawn on, and 44 is what that comes to
 * on grass. Held as an absolute on every plate it is the same khaki wherever it
 * lands - measured on the round-two shots, cavern ran an olive `(104,104,60)`
 * line over rust-red ground `(105,44,35)`, a near-complementary clash, and lava
 * ran chroma 44 over a board whose own chroma is 6, which made the hex web the
 * largest saturated element in a frame full of cold slate.
 *
 * So the line takes the **ground's own hue** and moves only value and
 * saturation. `GROUND_TONES.mid` is that hue; its deviation from its own luma is
 * the plate's chroma vector, and half of it is what the line keeps. Half rather
 * than all because a line is a mark on the ground and not more of it, and half
 * lands grass - the one plate the reference can be checked against - at R-B 40
 * against the reference's 44.
 *
 * The vector is luma-neutral by construction (its three components are weighted
 * deviations from the luma of the colour they came from, so Rec.709 of the
 * deviation is exactly zero), which is why adding it costs the line none of the
 * contrast `GRID_LINE_LIFT` buys.
 */
const GRID_LINE_CHROMA = 0.5;

/**
 * The lattice over one arena's own board: the board's own hue, lifted.
 *
 * One rule, and snow is inside it rather than beside it. The line clears its
 * board by `GRID_LINE_LIFT` wherever the plate leaves room for it, and where it
 * does not - snow's ground is 216.7 and there is no colour at 263.7 - it takes
 * everything that is left instead of turning over.
 *
 * **The polarity does not flip any more, and that is the change.** The previous
 * rule sent the line *down* on the four brightest plates, by `GRID_LINE_LIFT +
 * REACH_DROP` so that its weaker side - the shaded third, where the ground comes
 * down to meet it - still cleared the reference's own figure. That kept the
 * shaded contrast at -47 and paid -83 over open ground, which is two thirds of
 * the board: measured on the round-two snow shot the line ran `(132,132,154)` at
 * luma 133.6 against a 217.0 snowfield, 1.8x the reference's open-ground figure
 * and visibly the loudest hex web of the seven. Clamping instead gives snow a
 * white line at +38.3 over its open ground and +74.3 over its shaded ground,
 * against the reference's +47 and +75 - short on the open side rather than long,
 * and the same polarity as the other six, so no board on the roster carries a
 * lattice that inverts against its neighbour's.
 *
 * Decided once per arena off the plate, so the polarity can never change in the
 * middle of a field.
 */
export function gridLineColor(terrain: string): number {
  const mid = groundTones(terrain).mid;
  const ml = luma(mid);
  const want = Math.min(255, groundLevel(terrain) + GRID_LINE_LIFT);
  const chan = (shift: number): number => {
    const dev = ((mid >> shift) & 255) - ml;
    return Math.max(0, Math.min(255, Math.round(want + GRID_LINE_CHROMA * dev)));
  };
  return (chan(16) << 16) | (chan(8) << 8) | chan(0);
}

/**
 * The lattice over the one arena everything else on this screen is calibrated
 * to, kept as a constant so a test can state the line without naming a terrain.
 *
 * It is `(152,138,123)`, Rec.709 luma 139.9, over a dirt board at 93.1: +46.8,
 * the reference's +47. It lives here rather than in `theme.ts`'s `COLOR`
 * because this is the file that calibrates it and the file whose spec pins it.
 *
 * **It is derived now rather than written down.** It was the absolute
 * `0xc0c094`, and an absolute lattice cannot be a lattice on twenty-one arenas
 * whose own ground runs from luma 26 to 217: held at 188.8 it cleared its board
 * by +160 on lava and by -28 on snow, and a marking whose polarity depends on
 * which arena is being fought on is not one marking.
 *
 * What the seven shipped arenas now solve to, line against their own open
 * ground - the figure `battle-grid.spec.ts` states for dirt alone:
 *
 *   lava       (70,74,74)     +46.7
 *   cavern     (127,93,88)    +46.5
 *   wasteland  (142,138,125)  +47.1
 *   dirt       (152,138,123)  +46.8
 *   highlands  (139,143,118)  +47.0
 *   snow       (255,255,255)  +38.3
 *
 * one contrast on six of them and the seventh short because its plate has no
 * room for more, where the shipped lattice ran +128 on four boards and -60 on
 * snow.
 */
export const GRID_LINE_COLOR = gridLineColor("dirt");

/**
 * The board grid is **opaque**, and this constant exists so that a test can say
 * so against an external anchor rather than against the line below it.
 *
 * An alpha-blended line's contrast is `alpha * (colour - ground)`, so it fades
 * exactly where the ground rises toward its own colour - which on this screen is
 * the whole unshaded third of the field. The classic's grid holds one value across a
 * 27.8-luma step in its own ground (`classic16.jpg`, the header); ours is flat
 * everywhere. `battle-grid.spec.ts` checks the contrast this yields over both
 * of the field's measured ground levels.
 */
export const GRID_LINE_ALPHA = 1;
/**
 * Grid line width in arena pixels: 1, which is 1.5 device px at 1080p and 1.07
 * at the laptop viewport.
 *
 * Relative to the cell it draws - and the cell is the only ruler that survives a
 * change of resolution - that is 1.5/90 = 1.7% of a column pitch, against the expansion's
 * 1/48 = 2.1% on the wasteland reference. A hair finer than the reference rather
 * than heavier, which is the right side to err on for a lattice that is now
 * drawn over the whole board.
 */
export const GRID_LINE_W = 1;

/**
 * The cell outlines the reach marking claims: **one per reachable cell**.
 *
 * Exported because this is the defect the per-cell treatment fixed, in one line.
 * The marking used to stroke `regionEdges` alone - the boundary of the union -
 * and since obstacle cells are holes in that union, the boundary spends most of
 * its length drawing a hexagon around each rock. What a player then sees marked
 * is precisely the cells they cannot enter.
 *
 * These lines are now drawn as part of `BOARD_CELL_EDGES` rather than by a pass
 * of their own: the board grid already puts an identical line on every one of
 * them, so a second stroke would only double the coverage on the reachable third
 * of the lattice and leave a seam where it stopped. The helper stays because it
 * is the *statement* of which cells the marking covers, and `battle-reach.spec`
 * asserts against it.
 */
export function reachCellOutlines(hexes: readonly number[]): readonly (readonly number[])[] {
  return hexes.map((h) => hexPolygon(asHex(h)));
}

/**
 * How far each shaded cell is inset from its outline, in arena pixels.
 *
 * **Zero.** It was 0.75, on the argument that the classic's `CCellShd` mask stops
 * short of the cell edge and leaves a hairline of open ground between shaded
 * neighbours. Measured on the shipped 1080p dirt shot (scanline y=612, x
 * 500..900) that hairline came out at luma 114 against a shaded interior of 57
 * and open ground of 89 - the seams were *brighter than the field*, because
 * the inset bared the lit lip of the ground detail under every edge, and with
 * `antialias: false` a 0.75px inset is a whole device pixel of it. At rest
 * the reach read as a drawn grid, and on a siege where the acting stack flies
 * to every cell the entire board was gridded with `showGrid` off.
 *
 * The classic's own marking, re-read on a reference screenshot: a uniform darkening with the
 * cell boundaries only faintly visible where two darkened hexes abut, and no
 * lit line anywhere. Cells that share an edge share its vertices exactly
 * (`cell()` builds every polygon from the same integer pitch), so at zero the
 * rasteriser covers each seam pixel once and the region is one shade.
 * `battle-reach.spec.ts` pins this at zero.
 */
export const REACH_SEAM = 0;

/**
 * The movement marking: **the cells the acting stack can walk to, multiplied
 * down**, one cell at a time.
 *
 * This is the classic's polarity, and for a while it was not ours. The marking was
 * painted on the complement - every cell the stack could *not* reach, flooded
 * inward from the board's rim - on the argument that a marking which puts the
 * invitation in shadow is inside-out. It is also what the reference does:
 * on a reference screenshot the High Radiants are
 * acting and the darkened region is the ground they can fly to, with the
 * unreachable strip beside the Elves left in the light. A player who knows
 * the game reads dark as "mine to move to", and a board that reads the other
 * way is a board that reads wrong, however it is calibrated.
 *
 * The complement also cost a picture. A stack of speed 7 in the centre of a
 * board reaches two thirds of it, so two thirds of the field went under one
 * flat shade with a stair-stepped edge across the middle - the largest single
 * shape on the screen, and not one the classic draws. Cell by cell, with the seam
 * `REACH_SEAM` leaves, the same ground reads as a set of marked hexes.
 *
 * No rim, no lip: the reference has none. The depth is `reachShadeAlpha`, the
 * per-arena solve for the classic's own 36-luma drop.
 */
function paintReachRegion(g: Graphics, hexes: readonly number[], terrain: string): void {
  if (hexes.length === 0) return;
  for (const h of hexes) {
    const poly = hexPolygon(asHex(h));
    g.poly(REACH_SEAM > 0 ? insetPolygon(poly, REACH_SEAM) : [...poly], true);
  }
  g.fill({ color: REACH_SHADE, alpha: reachShadeAlpha(terrain) });
  // The seams: one darker hairline on every edge two shaded cells share, and
  // on no other edge. The classic's marking (a reference screenshot) is a set of
  // darkened hexes whose boundaries show faintly where they abut; without
  // them ours was one merged blob over 62% of the field whose only structure
  // was its serrated outline, and on a dirt plate that reads as a mud stain.
  // Darker than the shade, never lighter: a lit seam is a drawn grid (the
  // 0.75 inset this file once used bared the ground's lit lip and measured
  // brighter than the open field), and the region's own outer boundary carries
  // no line because the reference's does not.
  const marked = new Set<number>(hexes);
  const near: number[] = [];
  for (const h of hexes) {
    const poly = hexPolygon(asHex(h));
    const c = hexCentre(asHex(h));
    const n = hexNeighbours(h, near);
    for (let k = 0; k < n; k++) {
      const o = near[k]!;
      // Each shared edge once: the lower index draws it.
      if (o <= h || !marked.has(o)) continue;
      const oc = hexCentre(asHex(o));
      const mx = (c.x + oc.x) / 2, my = (c.y + oc.y) / 2;
      // The polygon edge whose midpoint is nearest the two centres' midpoint
      // is the shared one; the cells are built from one integer pitch, so it
      // is exact to a fraction of a pixel.
      let best = 0, bestD = Infinity;
      for (let e = 0; e < 6; e++) {
        const ex = (poly[e * 2]! + poly[((e + 1) % 6) * 2]!) / 2;
        const ey = (poly[e * 2 + 1]! + poly[((e + 1) % 6) * 2 + 1]!) / 2;
        const d = (ex - mx) * (ex - mx) + (ey - my) * (ey - my);
        if (d < bestD) { bestD = d; best = e; }
      }
      g.moveTo(poly[best * 2]!, poly[best * 2 + 1]!)
        .lineTo(poly[((best + 1) % 6) * 2]!, poly[((best + 1) % 6) * 2 + 1]!);
    }
  }
  g.stroke({ width: 1, color: REACH_SHADE, alpha: REACH_SEAM_ALPHA, alignment: 0.5 });
}

/**
 * How much darker than the shaded cell its seams are, as a second multiply on
 * a 1px line. 0.28 over a dirt board shaded to 58 luma puts the seam at about
 * 42: visible as structure, invisible as a line at arm's length, which is
 * where the reference's own cell boundaries sit.
 */
// 0.14, down from 0.28: hovering one cell with a speed-12 flyer active marks
// most of the board, and at 0.28 the seams between the marked cells drew the
// whole lattice, which the classic only shows with the grid switched on.
export const REACH_SEAM_ALPHA = 0.14;

/**
 * Mud and embers: the ground hazards, shown only when asked for.
 *
 * The arena used to paint them at rest - two flat rings and three red coals
 * per cell - and a board with three of those on it read as cluttered rather
 * than as terrain; the classic keeps its own ground hazards hidden until something
 * steps in them. They are still real (mud costs two movement points, embers
 * burn), so they are drawn with the grid overlay, which is the player's
 * "show me the board" toggle, and always under the pointer, so a cell that is
 * about to be clicked says what it is. Flat and in the cell, no silhouette,
 * because a hazard is ground a stack may enter and must not look like cover.
 */
function paintHazards(g: Graphics, state: BattleState, ui: UiState): void {
  for (const o of state.obstacles) {
    if (o.kind !== "mud" && o.kind !== "embers") continue;
    if (ui.showGrid !== true && ui.hoverHex !== o.hex) continue;
    const poly = hexPolygon(asHex(o.hex));
    const c = hexCentre(asHex(o.hex));
    if (o.kind === "mud") {
      g.poly(insetPolygon(poly, 4), true).fill({ color: 0x4b3f2c, alpha: 0.5 });
      g.poly(insetPolygon(poly, 13), true).fill({ color: 0x342b1d, alpha: 0.45 });
    } else {
      g.poly(insetPolygon(poly, 4), true).fill({ color: 0x3b2018, alpha: 0.55 });
      for (let i = 0; i < 3; i++) {
        g.circle(c.x + (i - 1) * 9, c.y + 2 - (i & 1) * 3, 3).fill({ color: 0xb5401c, alpha: 0.85 });
      }
    }
  }
}

/** A convex cell outline shrunk toward its own centroid by `d` pixels. */
function insetPolygon(poly: readonly number[], d: number): number[] {
  const n = poly.length >> 1;
  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) { cx += poly[i * 2]!; cy += poly[i * 2 + 1]!; }
  cx /= n; cy /= n;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = poly[i * 2]!, y = poly[i * 2 + 1]!;
    const len = Math.max(1e-6, Math.hypot(x - cx, y - cy));
    out.push(x - ((x - cx) / len) * d, y - ((y - cy) / len) * d);
  }
  return out;
}

/**
 * The board's own lattice: one opaque line on every cell edge, everywhere.
 *
 * Drawn after the reach shading and after the stand-from patch, so the same
 * line lies over the darkened ground and the open ground both. See
 * `GRID_LINE_COLOR` for the measurement it is calibrated against, and the
 * header of this file for what drawing it only inside the reach did to the
 * picture.
 *
 * `BOARD_CELL_EDGES` rather than 165 stroked polygons: coincident passes on an
 * antialiased hairline are a heavier line, and every interior edge belongs to
 * two cells.
 */
function paintBoardGrid(g: Graphics, terrain: string): void {
  for (const e of BOARD_CELL_EDGES) g.moveTo(e[0]!, e[1]!).lineTo(e[2]!, e[3]!);
  g.stroke({
    width: GRID_LINE_W, color: gridLineColor(terrain), alpha: GRID_LINE_ALPHA, alignment: 0.5,
  });
}

const signed = (v: number): string => (v > 0 ? `+${v}` : String(v));

/**
 * FNV-1a over a string, so a stand-in commander is the same commander on every
 * frame and every machine. Not a hash for anything that matters - it picks an
 * index into a sorted roster and nothing else.
 */
function hashKey(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h = (h ^ s.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * `PORTRAITS/assets/<faction>/<faction>_<name>.png` -> `portrait.<faction>.<name>`.
 *
 * The same rule as `packages/ui/src/theme/art.ts`, restated because `@game/render`
 * does not depend on `@game/ui` and must not start to for one path split.
 */
function portraitKeyOf(art: string): string | null {
  const parts = art.split("/");
  const faction = parts[parts.length - 2];
  const file = parts[parts.length - 1];
  if (faction === undefined || file === undefined) return null;
  const dot = file.lastIndexOf(".");
  const base = dot > 0 ? file.slice(0, dot) : file;
  const name = base.startsWith(`${faction}_`) ? base.slice(faction.length + 1) : base;
  return `portrait.${faction}.${name}`;
}
